/**
 * Wishlist Sidebar Drawer
 * Opens from the header heart button, shows wishlist items with
 * variant/size selector, quantity stepper, add-to-cart, buy-now, and remove.
 *
 * Restock tracking: compares current product availability against a localStorage
 * snapshot saved when the drawer was last closed. Items that were OOS and are
 * now available appear at the top with a dismissable "Back in Stock" badge.
 * The header badge stays in sync until a restock notice is dismissed or handled.
 */
(function () {
  "use strict";

  if (window.__mrMapleWishlistDrawerBootstrapped) {
    return;
  }
  window.__mrMapleWishlistDrawerBootstrapped = true;

  const API_BASE = "/apps/garden";
  const STORAGE_KEY = "wl_avail_v1";
  const MISSING_PRODUCTS_KEY = "wl_missing_products_v1";
  const QUANTITY_STORAGE_KEY = "wl_drawer_qty_v1";
  const PRIMARY_WISHLIST_LIST_ID = "wishlist-default";
  const MISSING_PRODUCTS_TTL_MS = 6 * 60 * 60 * 1000;
  const QUANTITY_STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const PRODUCT_CACHE = new Map();
  const DELETE_ITEM_REQUESTS = new Map();
  const DELETED_ITEM_TOMBSTONES = new Set();
  const HEADER_ANIMATION_TIMERS = new WeakMap();
  const WISHLIST_HEADER_CELEBRATE_EVENT = "wl:header-wishlist:celebrate";
  const LISTS_CACHE_TTL_MS = 30 * 1000;
  const LIST_ITEMS_CACHE_TTL_MS = 2 * 60 * 1000;
  const SHARED_LISTS_CACHE_TTL_MS = 5 * 60 * 1000;
  const SHARED_LISTS_CACHE_PERSIST_MS = 24 * 60 * 60 * 1000;
  const PRIMARY_WISHLIST_AVAILABILITY_TTL_MS = 60 * 1000;
  const DRAWER_PAGE_SIZE = 20;
  const DRAWER_PREFETCH_THRESHOLD = 4;
  const MAX_WISHLIST_DRAWER_QUANTITY = 999;
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const PRODUCT_REQUESTS = new Map();
  const PICKUP_ONLY_PATTERNS = [
    /^\s*FOR\s+PICK\s*UP\s+ONLY\s*\|\s*/i,
    /^\s*PICK\s*UP\s+ONLY\s*\|\s*/i,
    /^\s*FOR\s+PICKUP\s+ONLY\s*\|\s*/i,
    /^\s*PICKUP\s+ONLY\s*\|\s*/i,
    /\s*\|\s*DOES\s+NOT\s+SHIP\s*$/i,
    /\s*DOES\s+NOT\s+SHIP\s*$/i,
  ];

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function formatMoney(cents) {
    if (typeof cents !== "number") return "";
    return "$" + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  // Normalize a variant money value to integer cents.
  // The backend is inconsistent across endpoints: `/v1/lists` serializes
  // productData prices to integer cents (number), while `/v1/lists/:id/items`
  // and `/v1/wishlist/items` attach raw Shopify Money strings in dollars
  // (e.g. "60.00"). Numbers are treated as cents; strings as dollar amounts.
  function variantMoneyToCents(value) {
    if (typeof value === "number") {
      return Number.isFinite(value) ? Math.round(value) : 0;
    }
    if (typeof value === "string") {
      const dollars = parseFloat(value.trim());
      return Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;
    }
    return 0;
  }

  function parseWishlistItemTitle(rawTitle) {
    const originalTitle = String(rawTitle || "").trim();
    if (!originalTitle) {
      return {
        cleanTitle: "Untitled Product",
        isPickupOnly: false,
      };
    }

    let cleanTitle = originalTitle;
    let isPickupOnly = false;

    PICKUP_ONLY_PATTERNS.forEach((pattern) => {
      const nextTitle = cleanTitle.replace(pattern, "");
      if (nextTitle !== cleanTitle) {
        isPickupOnly = true;
        cleanTitle = nextTitle;
      }
    });

    cleanTitle = cleanTitle
      .replace(/^\s*\|\s*/g, "")
      .replace(/\s*\|\s*$/g, "")
      .replace(/\s{2,}/g, " ")
      .trim();

    return {
      cleanTitle: cleanTitle || originalTitle,
      isPickupOnly,
    };
  }

  function imgSrc(url, size) {
    if (!url) return "";
    return url.replace(/(\.[^.]+)$/, `_${size}x$1`).replace(/\?.+$/, "");
  }

  function normalizeImageCandidate(candidate) {
    if (!candidate) return "";

    if (typeof candidate === "string") {
      return candidate.trim();
    }

    if (typeof candidate !== "object") {
      return "";
    }

    const direct =
      candidate.src ||
      candidate.url ||
      candidate.imageUrl ||
      candidate.originalSrc ||
      candidate.transformedSrc ||
      candidate.preview_image?.src ||
      candidate.previewImage?.url ||
      candidate.image?.src ||
      candidate.image?.url;

    if (typeof direct === "string" && direct.trim()) {
      return direct.trim();
    }

    return "";
  }

  function collectImageCandidates(value, candidates) {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => collectImageCandidates(entry, candidates));
      return;
    }
    candidates.push(value);
  }

  function buildListsUrl(options = {}) {
    const config =
      typeof options === "boolean"
        ? { includeItems: true, includeProductData: options }
        : options || {};
    const {
      includeItems = false,
      includeProductData = false,
      limit = 50,
      offset = 0,
    } = config;
    const params = new URLSearchParams();
    params.set("includeItems", includeItems ? "1" : "0");
    if (includeProductData) {
      params.set("includeProductData", "1");
    }
    if (Number.isFinite(limit)) {
      params.set("limit", String(Math.max(1, Math.min(50, Math.trunc(limit)))));
    }
    if (Number.isFinite(offset) && offset > 0) {
      params.set("offset", String(Math.trunc(offset)));
    }
    return `${API_BASE}/v1/lists?${params.toString()}`;
  }

  function buildListItemsUrl(listId) {
    return `${API_BASE}/v1/lists/${encodeURIComponent(listId)}/items`;
  }

  function buildDeleteItemKey(listId, itemId) {
    const normalizedListId = String(listId || "").trim();
    const normalizedItemId = String(itemId || "").trim();
    return normalizedListId && normalizedItemId
      ? `${normalizedListId}:${normalizedItemId}`
      : "";
  }

  function buildPrimaryWishlistItemsUrl(options = {}) {
    const params = new URLSearchParams();
    const parsedLimit = Number.parseInt(options.limit, 10);
    const limit = Number.isFinite(parsedLimit) && parsedLimit > 0
      ? Math.max(1, Math.min(100, parsedLimit))
      : DRAWER_PAGE_SIZE;
    params.set("limit", String(limit));
    if (options.cursor) {
      params.set("cursor", String(options.cursor));
    }
    return `${API_BASE}/v1/wishlist/items?${params.toString()}`;
  }

  function buildPrimaryWishlistAvailabilityUrl() {
    return `${API_BASE}/v1/wishlist/availability`;
  }

  function getSharedGardenCache() {
    return window.MrMapleGardenCache || null;
  }

  function getWishlistCacheRootElement() {
    return (
      document.querySelector(".wl-container[data-app-proxy-base]") ||
      document.querySelector("[data-wishlist-root][data-app-proxy-base]") ||
      document.querySelector("[data-app-proxy-base]")
    );
  }

  function getWishlistCacheOptions(overrides = {}) {
    const root = getWishlistCacheRootElement();
    const analyticsPage = window?.ShopifyAnalytics?.meta?.page;
    const customerId =
      overrides.customerId ||
      root?.dataset?.customerId ||
      analyticsPage?.customerId ||
      analyticsPage?.customer_id ||
      window?.Shopify?.customer?.id ||
      window?.ShopifyCustomer?.id ||
      undefined;

    return {
      shop:
        overrides.shop ||
        root?.dataset?.shop ||
        window?.Shopify?.shop ||
        undefined,
      customerId:
        customerId === null || customerId === undefined
          ? undefined
          : String(customerId),
      ...overrides,
    };
  }

  function hasExplicitSignedOutCustomer() {
    return (
      window.MrMapleCustomer &&
      window.MrMapleCustomer.isAuthenticated === false
    );
  }

  function isWishlistCustomerAuthenticated() {
    if (hasExplicitSignedOutCustomer()) return false;
    if (window.MrMapleCustomer?.isAuthenticated === true) return true;
    if (window.MrMapleCustomer?.id) return true;
    if (window.ShopifyCustomer?.id) return true;
    return Boolean(getWishlistCacheOptions().customerId || window.__st?.cid);
  }

  function buildWishlistLoginUrl(returnTo) {
    const fallbackTarget =
      window.location.pathname + window.location.search + window.location.hash;
    let target = returnTo || fallbackTarget || "/";

    try {
      const resolved = new URL(target, window.location.origin);
      target =
        resolved.origin === window.location.origin
          ? `${resolved.pathname}${resolved.search}${resolved.hash}`
          : fallbackTarget || "/";
    } catch {
      target = fallbackTarget || "/";
    }

    const loginUrl =
      window.Theme?.routes?.account_login_url || "/account/login";
    return `${loginUrl}?return_url=${encodeURIComponent(target)}`;
  }

  function buildSharedListsCacheModule(options = {}) {
    if (typeof options === "boolean") {
      return `wishlist:lists:${options ? "true" : "false"}`;
    }

    const { includeItems = false, includeProductData = false } = options || {};
    if (includeItems && includeProductData) return "wishlist:lists:true";
    if (includeItems) return "wishlist:lists:items";
    return "wishlist:lists:summary";
  }

  function getSharedListsCacheMetadata(options = {}) {
    return {
      module: buildSharedListsCacheModule(options),
      domain: "lists",
      revisionDomain: "lists",
      tags: ["lists", "wishlist"],
      maxAgeMs: SHARED_LISTS_CACHE_TTL_MS,
      persistForMs: SHARED_LISTS_CACHE_PERSIST_MS,
    };
  }

  function getSharedListItemsCacheMetadata(listId) {
    return {
      module: `wishlist:list-items:${String(listId || "").trim()}`,
      domain: "lists",
      revisionDomain: "lists",
      tags: ["lists", "wishlist", "wishlistItems"],
      maxAgeMs: LIST_ITEMS_CACHE_TTL_MS,
      persistForMs: SHARED_LISTS_CACHE_PERSIST_MS,
    };
  }

  function getSharedPrimaryWishlistItemsCacheMetadata(options = {}) {
    const cursor = String(options.cursor || "").trim();
    const parsedLimit = Number.parseInt(options.limit, 10);
    const limit = Number.isFinite(parsedLimit) && parsedLimit > 0
      ? Math.max(1, Math.min(100, parsedLimit))
      : DRAWER_PAGE_SIZE;
    const pageKey = cursor ? `${limit}:${cursor}` : "";
    return {
      module: pageKey ? `wishlist:primary-items:${pageKey}` : "wishlist:primary-items",
      domain: "lists",
      revisionDomain: "lists",
      tags: ["lists", "wishlist", "wishlistItems"],
      maxAgeMs: LIST_ITEMS_CACHE_TTL_MS,
      persistForMs: SHARED_LISTS_CACHE_PERSIST_MS,
    };
  }

  function getSharedPrimaryWishlistAvailabilityCacheMetadata() {
    return {
      module: "wishlist:primary-availability",
      domain: "lists",
      revisionDomain: "lists",
      tags: ["lists", "wishlist", "wishlistAvailability"],
      maxAgeMs: PRIMARY_WISHLIST_AVAILABILITY_TTL_MS,
      persistForMs: SHARED_LISTS_CACHE_PERSIST_MS,
    };
  }

  function extractResourceId(value) {
    const normalized = String(value || "").trim();
    if (!normalized) return "";
    const lastSlashIndex = normalized.lastIndexOf("/");
    return lastSlashIndex >= 0 ? normalized.slice(lastSlashIndex + 1) : normalized;
  }

  function getWishlistItemId(item) {
    if (!item || typeof item !== "object") return "";
    const raw = item.id ?? item.itemId ?? item.listItemId ?? item.item_id;
    return raw === null || raw === undefined ? "" : String(raw).trim();
  }

  function hasExplicitWishlistItemQuantity(item) {
    if (!item || typeof item !== "object") return false;
    return (
      Object.prototype.hasOwnProperty.call(item, "quantity") ||
      Object.prototype.hasOwnProperty.call(item, "qty") ||
      Object.prototype.hasOwnProperty.call(item, "desiredQuantity")
    );
  }

  function normalizeWishlistItem(item) {
    if (!item || typeof item !== "object") return item;
    const resolvedId = getWishlistItemId(item);
    const quantity = getWishlistItemQuantity(item);
    const normalized = resolvedId
      ? { ...item, id: resolvedId, itemId: resolvedId }
      : { ...item };
    normalized.quantity = quantity;
    return normalized;
  }

  function dedupeWishlistItems(items) {
    const deduped = [];
    (Array.isArray(items) ? items : [])
      .map(normalizeWishlistItem)
      .filter(Boolean)
      .forEach((item) => {
        const existingIndex = deduped.findIndex((entry) =>
          wishlistItemsMatchIdentity(entry, item),
        );
        if (existingIndex >= 0) {
          deduped[existingIndex] = normalizeWishlistItem({
            ...deduped[existingIndex],
            ...item,
          });
          return;
        }
        deduped.push(item);
      });
    return deduped;
  }

  function normalizeWishlistProductReference(value) {
    return extractResourceId(value);
  }

  function wishlistItemsMatchProduct(left, right) {
    const leftProductId = normalizeWishlistProductReference(
      left?.productId ?? left?.productGid,
    );
    const rightProductId = normalizeWishlistProductReference(
      right?.productId ?? right?.productGid,
    );
    if (leftProductId && rightProductId) {
      return leftProductId === rightProductId;
    }

    const leftHandle = String(left?.handle || left?.productHandle || "").trim().toLowerCase();
    const rightHandle = String(right?.handle || right?.productHandle || "").trim().toLowerCase();
    return Boolean(leftHandle && rightHandle && leftHandle === rightHandle);
  }

  function wishlistItemsMatchIdentity(item, detail) {
    const itemId = getWishlistItemId(item);
    const detailItemId = getWishlistItemId(detail?.item) || getWishlistItemId(detail);
    if (itemId && detailItemId && itemId === detailItemId) {
      return true;
    }
    return wishlistItemsMatchProduct(item, {
      productId: detail?.productId ?? detail?.item?.productId,
      productGid: detail?.productGid ?? detail?.item?.productGid,
      handle: detail?.handle ?? detail?.productHandle ?? detail?.item?.handle ?? detail?.item?.productHandle,
      variantId: detail?.variantId ?? detail?.item?.variantId,
    });
  }

  function getWishlistItemQuantity(item) {
    const raw = item?.quantity ?? item?.qty ?? item?.desiredQuantity;
    const parsed =
      typeof raw === "number" && Number.isFinite(raw)
        ? raw
        : Number.parseInt(String(raw || ""), 10);
    if (!Number.isFinite(parsed) || parsed < 1) return 1;
    return Math.min(MAX_WISHLIST_DRAWER_QUANTITY, Math.trunc(parsed));
  }

  function loadQuantityStorage() {
    try {
      const parsed = JSON.parse(localStorage.getItem(QUANTITY_STORAGE_KEY) || "{}");
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  function saveQuantityStorage(state) {
    try {
      localStorage.setItem(QUANTITY_STORAGE_KEY, JSON.stringify(state || {}));
    } catch {}
  }

  function buildQuantityStorageKey(listId, itemId) {
    const normalizedListId = String(listId || PRIMARY_WISHLIST_LIST_ID).trim();
    const normalizedItemId = String(itemId || "").trim();
    return normalizedItemId ? `${normalizedListId}:${normalizedItemId}` : "";
  }

  function getStoredWishlistQuantity(listId, itemId) {
    const key = buildQuantityStorageKey(listId, itemId);
    if (!key) return null;
    const state = loadQuantityStorage();
    const entry = state[key];
    if (!entry || typeof entry !== "object") return null;
    if (Date.now() - (Number(entry.updatedAt) || 0) > QUANTITY_STORAGE_TTL_MS) {
      delete state[key];
      saveQuantityStorage(state);
      return null;
    }
    return getWishlistItemQuantity(entry);
  }

  function setStoredWishlistQuantity(listId, itemId, quantity) {
    const key = buildQuantityStorageKey(listId, itemId);
    if (!key) return;
    const nextQuantity = getWishlistItemQuantity({ quantity });
    const state = loadQuantityStorage();
    state[key] = {
      quantity: nextQuantity,
      updatedAt: Date.now(),
    };
    saveQuantityStorage(state);
  }

  function clearStoredWishlistQuantity(listId, itemId) {
    const key = buildQuantityStorageKey(listId, itemId);
    if (!key) return;
    const state = loadQuantityStorage();
    if (Object.prototype.hasOwnProperty.call(state, key)) {
      delete state[key];
      saveQuantityStorage(state);
    }
  }

  function normalizeWishlistListName(value) {
    return String(value || "").trim().toLowerCase();
  }

  function normalizeWishlistOwnership(list) {
    if (!list || typeof list !== "object") return "owner";
    return list.isShared === true || list.ownership === "shared"
      ? "shared"
      : "owner";
  }

  function isOwnedWishlistList(list) {
    return normalizeWishlistOwnership(list) === "owner";
  }

  function canUseWishlistListInDrawer(list) {
    if (!list || typeof list !== "object") return false;
    if (!isOwnedWishlistList(list)) return false;
    if (list.specialType === "my-garden") return false;
    if (list.specialType === "ordered" || list.isOrdered === true) return false;
    return true;
  }

  function isNamedWishlistList(list, desiredName = "Wishlist") {
    if (!list || typeof list !== "object") return false;
    if (list.specialType === "wishlist") return true;
    return normalizeWishlistListName(list.name) === normalizeWishlistListName(desiredName);
  }

  function findPrimaryWishlistList(lists, desiredName = "Wishlist") {
    const sourceLists = Array.isArray(lists) ? lists : [];
    const ownedLists = sourceLists.filter(canUseWishlistListInDrawer);

    return (
      ownedLists.find((list) => list.specialType === "wishlist") ||
      ownedLists.find((list) => isNamedWishlistList(list, desiredName)) ||
      ownedLists.find((list) => list.isDefault === true) ||
      ownedLists[0] ||
      sourceLists.find((list) => isNamedWishlistList(list, desiredName)) ||
      sourceLists.find((list) => list?.isDefault === true) ||
      sourceLists[0] ||
      null
    );
  }

  function getItemAddedAtValue(item) {
    const raw = item?.addedAt ?? item?.createdAt ?? item?.updatedAt;
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    if (typeof raw === "string" && raw.trim()) {
      const numeric = Number(raw);
      if (Number.isFinite(numeric)) return numeric;
      const parsed = Date.parse(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  function toProductSnapshot(productData) {
    if (!productData || typeof productData !== "object") {
      return null;
    }

    const variants = Array.isArray(productData.variants)
      ? productData.variants.map((variant) => ({
          id: extractResourceId(variant?.id),
          title: variant?.title,
          available:
            variant?.available === true || variant?.availableForSale === true,
          price: variantMoneyToCents(variant?.price),
          compare_at_price:
            variant?.compare_at_price != null
              ? variantMoneyToCents(variant.compare_at_price)
              : variant?.compareAtPrice != null
                ? variantMoneyToCents(variant.compareAtPrice)
                : null,
          featured_image: normalizeImageCandidate(
            variant?.featured_image || variant?.featuredImage || variant?.image,
          )
            ? {
                src: normalizeImageCandidate(
                  variant?.featured_image || variant?.featuredImage || variant?.image,
                ),
              }
            : null,
        }))
      : [];

    return {
      id: extractResourceId(productData.id),
      handle: productData.handle || "",
      vendor: productData.vendor,
      product_type: productData.product_type || productData.productType,
      tags: Array.isArray(productData.tags) ? productData.tags : [],
      featured_image: productData.featured_image?.src
        ? { src: productData.featured_image.src }
        : productData.featuredImage?.url
          ? { src: productData.featuredImage.url }
          : null,
      images: Array.isArray(productData.images)
        ? productData.images.map(normalizeImageCandidate).filter(Boolean)
        : [],
      variants,
    };
  }

  function getItemEmbeddedProduct(item) {
    return toProductSnapshot(item?.productData);
  }

  function primeProductCacheFromItems(items) {
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      const handle = String(item?.handle || "").trim();
      const snapshot = getItemEmbeddedProduct(item);
      if (handle && snapshot && getProductImageUrl(item, snapshot)) {
        PRODUCT_CACHE.set(handle, snapshot);
      }
    });
  }

  function itemNeedsProductRefresh(item) {
    const handle = String(item?.handle || "").trim();
    if (!handle) return false;
    if (PRODUCT_CACHE.has(handle)) return false;
    const embeddedProduct = getItemEmbeddedProduct(item);
    return !embeddedProduct || !getProductImageUrl(item, embeddedProduct);
  }

  function itemNeedsProductImageRefresh(item) {
    const handle = String(item?.handle || "").trim();
    if (!handle) return false;
    if (PRODUCT_CACHE.has(handle)) return false;
    const embeddedProduct = getItemEmbeddedProduct(item);
    return !getProductImageUrl(item, embeddedProduct);
  }

  function itemNeedsFreshAvailabilityCheck(
    item,
    snapshot = {},
    persistedRestockedIds = [],
  ) {
    const itemId = String(item?.id || "").trim();
    const handle = String(item?.handle || "").trim();
    if (!itemId || !handle) return false;

    if (snapshot[itemId]?.available === false) {
      return true;
    }

    return Array.isArray(persistedRestockedIds)
      ? persistedRestockedIds.includes(itemId)
      : false;
  }

  function getResolvedProduct(item) {
    const handle = item?.handle;
    if (handle && PRODUCT_CACHE.has(handle)) {
      return PRODUCT_CACHE.get(handle) || null;
    }

    return getItemEmbeddedProduct(item);
  }

  function getResolvedVariants(product) {
    return Array.isArray(product?.variants) ? product.variants : [];
  }

  function getMatchingVariant(product, variantId) {
    const normalizedVariantId = extractResourceId(variantId);
    const variants = getResolvedVariants(product);
    if (!variants.length) return null;

    if (!normalizedVariantId) {
      return variants[0] || null;
    }

    return (
      variants.find(
        (variant) => extractResourceId(variant?.id) === normalizedVariantId,
      ) ||
      variants[0] ||
      null
    );
  }

  function isVariantAvailable(variant) {
    if (!variant || typeof variant !== "object") return false;
    if (typeof variant.available === "boolean") {
      return variant.available;
    }
    if (typeof variant.availableForSale === "boolean") {
      return variant.availableForSale;
    }
    return true;
  }

  function getVariantPriceCents(variant, fallbackPrice) {
    if (typeof variant?.price === "number" && Number.isFinite(variant.price)) {
      return variant.price;
    }

    if (typeof fallbackPrice === "number" && Number.isFinite(fallbackPrice)) {
      return Math.round(fallbackPrice * 100);
    }

    return 0;
  }

  function getVariantComparePriceCents(variant) {
    if (
      typeof variant?.compare_at_price === "number" &&
      Number.isFinite(variant.compare_at_price)
    ) {
      return variant.compare_at_price;
    }

    if (
      typeof variant?.compareAtPrice === "number" &&
      Number.isFinite(variant.compareAtPrice)
    ) {
      return variant.compareAtPrice;
    }

    return 0;
  }

  function getProductImageUrl(item, product) {
    const candidates = [
      item?.image,
      item?.imageUrl,
      item?.featuredImage,
      item?.featured_image,
      item?.productImage,
      item?.variantImage,
      item?.variant?.image,
      item?._cached?.image,
      item?._cached?.featuredImage,
      item?._cached?.featured_image,
      product?.featured_image,
      product?.featuredImage,
      product?.image,
    ];

    collectImageCandidates(item?.images, candidates);
    collectImageCandidates(item?.galleryImages, candidates);
    collectImageCandidates(item?.productImages, candidates);
    collectImageCandidates(item?._cached?.images, candidates);
    collectImageCandidates(item?._cached?.galleryImages, candidates);
    collectImageCandidates(product?.images, candidates);
    collectImageCandidates(product?.media, candidates);

    getResolvedVariants(product).forEach((variant) => {
      collectImageCandidates(variant?.featured_image, candidates);
      collectImageCandidates(variant?.featuredImage, candidates);
      collectImageCandidates(variant?.image, candidates);
      collectImageCandidates(variant?.images, candidates);
    });

    for (const candidate of candidates) {
      const image = normalizeImageCandidate(candidate);
      if (image) return image;
    }

    return "";
  }

  async function buildFetchError(res) {
    let message = `HTTP ${res.status}`;

    try {
      const contentType = res.headers.get("content-type") || "";
      if (
        contentType.includes("application/json") ||
        contentType.includes("text/javascript")
      ) {
        const payload = await res.json();
        const detail =
          payload?.description ||
          payload?.message ||
          (typeof payload?.errors === "string" ? payload.errors : "");

        if (detail) {
          message = String(detail);
        }
      } else {
        const text = (await res.text()).trim();
        if (text) {
          message = text;
        }
      }
    } catch {}

    const error = new Error(message);
    error.status = res.status;
    return error;
  }

  async function fetchJSON(url, options = {}) {
    const res = await fetch(url, {
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      ...options,
    });
    if (!res.ok) throw await buildFetchError(res);
    return res.json();
  }

  async function deleteWishlistDrawerItem(listId, itemId, context = {}) {
    const normalizedListId = String(listId || "").trim();
    const normalizedItemId = String(itemId || "").trim();
    if (!normalizedListId || !normalizedItemId) {
      throw new Error("Missing wishlist item identifiers");
    }

    const deleteKey = buildDeleteItemKey(normalizedListId, normalizedItemId);
    if (DELETED_ITEM_TOMBSTONES.has(deleteKey)) {
      console.info("[WishlistDrawer] remove skipped for already removed item", {
        listId: normalizedListId,
        itemId: normalizedItemId,
        context,
      });
      return { success: true, alreadyRemoved: true };
    }

    const pendingRequest = DELETE_ITEM_REQUESTS.get(deleteKey);
    if (pendingRequest) {
      console.info("[WishlistDrawer] remove joined pending request", {
        listId: normalizedListId,
        itemId: normalizedItemId,
        context,
      });
      return pendingRequest;
    }

    const pathUrl = `${buildListItemsUrl(normalizedListId)}/${encodeURIComponent(normalizedItemId)}`;
    const request = (async () => {
      console.info("[WishlistDrawer] remove request", {
        listId: normalizedListId,
        itemId: normalizedItemId,
        url: pathUrl,
        context,
      });

      try {
        const result = await fetchJSON(pathUrl, { method: "DELETE" });
        console.info("[WishlistDrawer] remove path endpoint success", {
          listId: normalizedListId,
          itemId: normalizedItemId,
          result,
        });
        return result;
      } catch (error) {
        console.warn("[WishlistDrawer] remove path endpoint failed", {
          listId: normalizedListId,
          itemId: normalizedItemId,
          url: pathUrl,
          status: error?.status,
          message: error?.message,
        });

        if (Number(error?.status) === 404) {
          console.info("[WishlistDrawer] remove already absent (treated as success)", {
            listId: normalizedListId,
            itemId: normalizedItemId,
          });
          return { success: true, alreadyRemoved: true };
        }
        throw error;
      }
    })();

    DELETE_ITEM_REQUESTS.set(deleteKey, request);
    try {
      const result = await request;
      DELETED_ITEM_TOMBSTONES.add(deleteKey);
      return result;
    } finally {
      DELETE_ITEM_REQUESTS.delete(deleteKey);
    }
  }

  async function querySharedListsCache(options = {}) {
    const {
      includeItems = false,
      includeProductData = false,
      force = false,
    } = options;
    const sharedGardenCache = getSharedGardenCache();
    if (!sharedGardenCache?.query) {
      return null;
    }

    const cacheOptions = getWishlistCacheOptions();
    if (!cacheOptions.customerId) {
      return null;
    }

    const metadata = getSharedListsCacheMetadata({
      includeItems,
      includeProductData,
    });
    const result = await sharedGardenCache.query(
      {
        ...metadata,
        refresh: force,
        fetcher: ({ headers = {} } = {}) =>
          fetch(buildListsUrl({ includeItems, includeProductData }), {
            credentials: "same-origin",
            headers: {
              Accept: "application/json",
              ...headers,
            },
          }),
      },
      { ...cacheOptions, ...metadata },
    );

    return result?.data || null;
  }

  async function querySharedListItemsCache(listId, options = {}) {
    const { force = false, allowStale = true } = options;
    const normalizedListId = String(listId || "").trim();
    if (!normalizedListId) return null;

    const sharedGardenCache = getSharedGardenCache();
    if (!sharedGardenCache?.query) {
      return null;
    }

    const cacheOptions = getWishlistCacheOptions();
    if (!cacheOptions.customerId) {
      return null;
    }

    const metadata = getSharedListItemsCacheMetadata(normalizedListId);
    const buildQueryInput = (refresh) => ({
        ...metadata,
        refresh,
        fetcher: ({ headers = {} } = {}) =>
          fetch(buildListItemsUrl(normalizedListId), {
            credentials: "same-origin",
            headers: {
              Accept: "application/json",
              ...headers,
            },
          }),
      });
    let result = await sharedGardenCache.query(
      buildQueryInput(force),
      { ...cacheOptions, ...metadata },
    );

    if (!allowStale && result?.stale === true && !force) {
      result = await sharedGardenCache.query(
        buildQueryInput(true),
        { ...cacheOptions, ...metadata },
      );
    }

    if (!allowStale && result?.stale === true) {
      return null;
    }

    return result?.data || null;
  }

  async function querySharedPrimaryWishlistItemsCache(options = {}) {
    const { force = false, allowStale = true, cursor = "", limit = DRAWER_PAGE_SIZE } = options;
    const sharedGardenCache = getSharedGardenCache();
    if (!sharedGardenCache?.query) {
      return null;
    }

    const cacheOptions = getWishlistCacheOptions();
    if (!cacheOptions.customerId) {
      return null;
    }

    const metadata = getSharedPrimaryWishlistItemsCacheMetadata({ cursor, limit });
    const buildQueryInput = (refresh) => ({
        ...metadata,
        refresh,
        fetcher: ({ headers = {} } = {}) =>
          fetch(buildPrimaryWishlistItemsUrl({ cursor, limit }), {
            credentials: "same-origin",
            headers: {
              Accept: "application/json",
              ...headers,
            },
          }),
      });
    let result = await sharedGardenCache.query(
      buildQueryInput(force),
      { ...cacheOptions, ...metadata },
    );

    if (!allowStale && result?.stale === true && !force) {
      result = await sharedGardenCache.query(
        buildQueryInput(true),
        { ...cacheOptions, ...metadata },
      );
    }

    if (!allowStale && result?.stale === true) {
      return null;
    }

    return result?.data || null;
  }

  async function querySharedPrimaryWishlistAvailabilityCache(options = {}) {
    const { force = false, allowStale = true } = options;
    const sharedGardenCache = getSharedGardenCache();
    if (!sharedGardenCache?.query) {
      return null;
    }

    const cacheOptions = getWishlistCacheOptions();
    if (!cacheOptions.customerId) {
      return null;
    }

    const metadata = getSharedPrimaryWishlistAvailabilityCacheMetadata();
    const result = await sharedGardenCache.query(
      {
        ...metadata,
        refresh: force,
        fetcher: ({ headers = {} } = {}) =>
          fetch(buildPrimaryWishlistAvailabilityUrl(), {
            credentials: "same-origin",
            headers: {
              Accept: "application/json",
              ...headers,
            },
          }),
      },
      { ...cacheOptions, ...metadata },
    );

    if (!allowStale && result?.stale === true) {
      return null;
    }

    return result?.data || null;
  }

  async function invalidateSharedWishlistCaches(options = {}) {
    const sharedGardenCache = getSharedGardenCache();
    if (!sharedGardenCache) {
      return;
    }

    const invalidateOptions = getWishlistCacheOptions();
    const tasks = [];

    if (typeof sharedGardenCache.invalidate === "function") {
      tasks.push(
        sharedGardenCache.invalidate(
          buildSharedListsCacheModule(false),
          {
            ...invalidateOptions,
            domain: "lists",
            revisionDomain: "lists",
            tags: ["lists", "wishlist"],
          },
        ),
      );
      tasks.push(
        sharedGardenCache.invalidate(
          buildSharedListsCacheModule(true),
          {
            ...invalidateOptions,
            domain: "lists",
            revisionDomain: "lists",
            tags: ["lists", "wishlist"],
          },
        ),
      );
      tasks.push(
        sharedGardenCache.invalidate(
          buildSharedListsCacheModule({ includeItems: false }),
          {
            ...invalidateOptions,
            domain: "lists",
            revisionDomain: "lists",
            tags: ["lists", "wishlist"],
          },
        ),
      );
      tasks.push(
        sharedGardenCache.invalidate(
          buildSharedListsCacheModule({ includeItems: true }),
          {
            ...invalidateOptions,
            domain: "lists",
            revisionDomain: "lists",
            tags: ["lists", "wishlist"],
          },
        ),
      );
      tasks.push(
        sharedGardenCache.invalidate("wishlist:primary-items", {
          ...invalidateOptions,
          domain: "lists",
          revisionDomain: "lists",
          tags: ["lists", "wishlist", "wishlistItems"],
        }),
      );
      tasks.push(
        sharedGardenCache.invalidate("wishlist:primary-availability", {
          ...invalidateOptions,
          domain: "lists",
          revisionDomain: "lists",
          tags: ["lists", "wishlist", "wishlistAvailability"],
        }),
      );

      if (options.invalidateProfile !== false) {
        tasks.push(
          sharedGardenCache.invalidate("profile", {
            ...invalidateOptions,
            domain: "profile",
          }),
        );
      }
    }

    if (typeof sharedGardenCache.invalidatePrefix === "function") {
      tasks.push(
        sharedGardenCache.invalidatePrefix("wishlist:lists:", {
          ...invalidateOptions,
          domain: "lists",
          revisionDomain: "lists",
          tags: ["lists", "wishlist"],
        }),
      );
      tasks.push(
        sharedGardenCache.invalidatePrefix("wishlist:list-items:", {
          ...invalidateOptions,
          domain: "lists",
          revisionDomain: "lists",
          tags: ["lists", "wishlist", "wishlistItems"],
        }),
      );
      tasks.push(
        sharedGardenCache.invalidatePrefix("wishlist:primary-items", {
          ...invalidateOptions,
          domain: "lists",
          revisionDomain: "lists",
          tags: ["lists", "wishlist", "wishlistItems"],
        }),
      );
      tasks.push(
        sharedGardenCache.invalidatePrefix("wishlist:primary-availability", {
          ...invalidateOptions,
          domain: "lists",
          revisionDomain: "lists",
          tags: ["lists", "wishlist", "wishlistAvailability"],
        }),
      );
      tasks.push(
        sharedGardenCache.invalidatePrefix("wishlist:status:", {
          ...invalidateOptions,
          domain: "wishlistStatus",
          revisionDomain: "lists",
          tags: ["wishlistStatus", "wishlist", "lists"],
        }),
      );
    }

    await Promise.allSettled(tasks);
  }

  async function getProduct(handle, options = {}) {
    const { force = false } = options;
    if (!handle) return null;
    if (!force && PRODUCT_CACHE.has(handle)) return PRODUCT_CACHE.get(handle);
    if (!force && PRODUCT_REQUESTS.has(handle)) {
      return PRODUCT_REQUESTS.get(handle);
    }
    if (force) {
      clearMissingProductHandle(handle);
      PRODUCT_REQUESTS.delete(handle);
    } else if (isMissingProductHandle(handle)) {
      return null;
    }

    const sharedLoader = window.MrMapleProductJsonLoader;
    const request = (async () => {
      try {
        const data = sharedLoader?.load
          ? await sharedLoader.load(handle, { force })
          : await fetchJSON(`/products/${encodeURIComponent(handle)}.js`);

        if (!data) {
          PRODUCT_CACHE.set(handle, null);
          markMissingProductHandle(handle);
          return null;
        }

        PRODUCT_CACHE.set(handle, data);
        clearMissingProductHandle(handle);
        return data;
      } catch (error) {
        PRODUCT_CACHE.set(handle, null);
        if (error?.status === 404) {
          markMissingProductHandle(handle);
        }
        return null;
      } finally {
        PRODUCT_REQUESTS.delete(handle);
      }
    })();

    PRODUCT_REQUESTS.set(handle, request);
    return request;
  }

  /**
   * Product prefetch runs through the global MrMapleProductJsonLoader queue
   * (4-wide). Raising concurrency here would just create slots that the shared
   * loader immediately rejects, so we defer to a single `Promise.all` and let
   * the loader serialize.
   */
  async function prefetchProductsForHandles(handles, _concurrency, options = {}) {
    const unique = [...new Set(handles.filter(Boolean))];
    if (unique.length === 0) return;
    await Promise.all(unique.map((handle) => getProduct(handle, options)));
  }

  function hasTrackedOosItems(snapshot) {
    return Object.values(snapshot || {}).some(
      (entry) => entry && entry.available === false,
    );
  }

  function dispatchWishlistListsUpdated(detail = {}) {
    document.dispatchEvent(
      new CustomEvent("wl:lists:updated", {
        detail: {
          source: "wishlist-drawer",
          domain: "lists",
          mutation: true,
          refreshClosedCount: true,
          ...detail,
        },
      }),
    );
  }

  function wishlistMutationProductRef(detail = {}) {
    return (
      detail.productId ??
      detail.productGid ??
      detail.item?.productId ??
      detail.item?.productGid ??
      ""
    );
  }

  function wishlistMutationVariantRef(detail = {}) {
    return (
      detail.variantId ??
      detail.item?.variantId ??
      detail.item?.variantGid ??
      ""
    );
  }

  function wishlistRefsMatch(left, right) {
    const leftRef = extractResourceId(left);
    const rightRef = extractResourceId(right);
    return Boolean(leftRef && rightRef && leftRef === rightRef);
  }

  function wishlistCardMatchesMutation(container, detail = {}) {
    if (!(container instanceof HTMLElement)) return false;
    const productRef = wishlistMutationProductRef(detail);
    if (!wishlistRefsMatch(container.dataset.productId, productRef)) {
      return false;
    }

    const mutationVariantRef = wishlistMutationVariantRef(detail);
    const cardVariantRef = container.dataset.variantId;
    if (!mutationVariantRef || !cardVariantRef) {
      return true;
    }

    return wishlistRefsMatch(cardVariantRef, mutationVariantRef);
  }

  function updateWishlistProductCards(detail = {}, isSaved) {
    document.querySelectorAll("[data-wishlist-card]").forEach((container) => {
      if (!wishlistCardMatchesMutation(container, detail)) return;
      const button = container.querySelector("[data-gallery-heart-btn]");
      if (!(button instanceof HTMLElement)) return;
      button.setAttribute("aria-pressed", isSaved ? "true" : "false");
      button.setAttribute(
        "aria-label",
        isSaved ? "Remove from Wishlist" : "Add to Wishlist",
      );
    });
  }

  function dispatchWishlistItemAdded(detail = {}) {
    const eventDetail = {
      source: "wishlist-drawer",
      ...detail,
    };
    updateWishlistProductCards(eventDetail, true);
    document.dispatchEvent(
      new CustomEvent("wl:item:added", {
        detail: eventDetail,
      }),
    );
  }

  function dispatchWishlistItemRemoved(detail = {}) {
    const eventDetail = {
      source: "wishlist-drawer",
      ...detail,
    };
    updateWishlistProductCards(eventDetail, false);
    document.dispatchEvent(
      new CustomEvent("wl:item:removed", {
        detail: eventDetail,
      }),
    );
  }

  // ─── LocalStorage helpers ────────────────────────────────────────────────────
  // Schema: { snapshot: { itemId: { available: bool, handle: string } }, newlyRestockedIds: string[] }

  function loadStorage() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    } catch {
      return {};
    }
  }

  function saveStorage(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {}
  }

  function loadMissingProducts() {
    try {
      const parsed = JSON.parse(localStorage.getItem(MISSING_PRODUCTS_KEY) || "{}");
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  function saveMissingProducts(data) {
    try {
      localStorage.setItem(MISSING_PRODUCTS_KEY, JSON.stringify(data));
    } catch {}
  }

  function pruneMissingProductsCache(cache) {
    const nextCache = cache && typeof cache === "object" ? { ...cache } : {};
    const now = Date.now();
    let changed = false;

    Object.keys(nextCache).forEach((handle) => {
      const expiresAt = Number(nextCache[handle]) || 0;
      if (expiresAt <= now) {
        delete nextCache[handle];
        changed = true;
      }
    });

    return { cache: nextCache, changed };
  }

  function isMissingProductHandle(handle) {
    const normalizedHandle = String(handle || "").trim().toLowerCase();
    if (!normalizedHandle) return false;

    const { cache, changed } = pruneMissingProductsCache(loadMissingProducts());
    if (changed) {
      saveMissingProducts(cache);
    }

    if (Number(cache[normalizedHandle]) > Date.now()) {
      return true;
    }

    const sharedLoader = window.MrMapleProductJsonLoader;
    return Boolean(sharedLoader?.isMissing?.(normalizedHandle));
  }

  function markMissingProductHandle(handle) {
    const normalizedHandle = String(handle || "").trim().toLowerCase();
    if (!normalizedHandle) return;

    const { cache } = pruneMissingProductsCache(loadMissingProducts());
    cache[normalizedHandle] = Date.now() + MISSING_PRODUCTS_TTL_MS;
    saveMissingProducts(cache);

    const sharedLoader = window.MrMapleProductJsonLoader;
    sharedLoader?.markMissing?.(normalizedHandle);
  }

  function clearMissingProductHandle(handle) {
    const normalizedHandle = String(handle || "").trim().toLowerCase();
    if (!normalizedHandle) return;

    const { cache } = pruneMissingProductsCache(loadMissingProducts());
    if (Object.prototype.hasOwnProperty.call(cache, normalizedHandle)) {
      delete cache[normalizedHandle];
      saveMissingProducts(cache);
    }

    const sharedLoader = window.MrMapleProductJsonLoader;
    sharedLoader?.clear?.(normalizedHandle);
  }

  // ─── Toast ──────────────────────────────────────────────────────────────────

  const Toast = {
    _el: null,
    _timer: null,
    init() {
      this._el = document.querySelector("[data-wl-toast]");
    },
    hide() {
      if (!this._el) return;
      this._el.classList.remove("wl-toast--visible", "wl-toast--busy");
      this._el.replaceChildren();
      if (this._timer) {
        clearTimeout(this._timer);
        this._timer = null;
      }
    },
    show(msg, type = "default", options = {}) {
      if (!this._el) return;

      const config =
        type && typeof type === "object"
          ? type
          : { ...options, type };
      const variant =
        typeof config.type === "string" && config.type.trim()
          ? config.type.trim()
          : "default";
      const duration = Number.isFinite(config.duration)
        ? Number(config.duration)
        : config.actionLabel
          ? 8000
          : 3000;

      this.hide();

      const messageEl = document.createElement("span");
      messageEl.className = "wl-drawer__toast-message";
      messageEl.textContent = msg;
      this._el.appendChild(messageEl);

      if (config.actionLabel && typeof config.onAction === "function") {
        const actionButton = document.createElement("button");
        actionButton.type = "button";
        actionButton.className = "wl-drawer__toast-action";
        actionButton.textContent = String(config.actionLabel);
        if (config.actionAriaLabel) {
          actionButton.setAttribute(
            "aria-label",
            String(config.actionAriaLabel),
          );
        }
        actionButton.addEventListener("click", async () => {
          if (actionButton.disabled) return;
          actionButton.disabled = true;
          this._el.classList.add("wl-toast--busy");
          try {
            await config.onAction();
            if (config.closeOnAction !== false) {
              this.hide();
            }
          } catch (error) {
            console.error("[WishlistDrawer] Toast action failed", error);
            actionButton.disabled = false;
            this._el.classList.remove("wl-toast--busy");
          }
        });
        this._el.appendChild(actionButton);
      }

      this._el.className = "wl-drawer__toast wl-toast--visible";
      if (variant !== "default") this._el.classList.add(`wl-toast--${variant}`);
      this._timer = setTimeout(() => {
        this.hide();
      }, duration);
    },
  };

  // ─── Drawer Core ────────────────────────────────────────────────────────────

  class WishlistDrawer {
    constructor() {
      this.drawer = document.querySelector("[data-wishlist-drawer]");
      this.panelEl = this.drawer?.querySelector("[data-wl-panel]");
      this.contentEl = this.drawer?.querySelector(".wl-drawer__content");
      this.itemsEl = this.drawer?.querySelector("[data-wl-items]");
      this.loadingEl = this.drawer?.querySelector("[data-wl-loading]");
      this.emptyEl = this.drawer?.querySelector("[data-wl-empty]");
      this.emptyTextEl = this.drawer?.querySelector(".wl-drawer__empty-text");
      this.emptyActionEl = this.drawer?.querySelector(".wl-drawer__empty-btn");
      this._defaultEmptyText =
        this.emptyTextEl?.textContent || "Your wishlist is empty";
      this._defaultEmptyActionText =
        this.emptyActionEl?.textContent || "Shop Now";
      this._defaultEmptyActionHref =
        this.emptyActionEl?.getAttribute("href") || "/collections/all";
      this.footerEl = this.drawer?.querySelector("[data-wl-footer]");
      this.filtersEl = this.drawer?.querySelector("[data-wl-filters]");
      this.countEl = this.drawer?.querySelector("[data-wl-drawer-count]");
      this._lastFocusedElement = null;
      this._boundHandleKeydown = this._handleKeydown.bind(this);

      this._listId = null;
      this._items = [];
      this._pendingWishlistAdds = [];
      this._pendingWishlistRemoves = [];
      this._removeRequests = new Set();
      this._open = false;
      // Set whenever local/shared caches are invalidated by a mutation so the
      // next _load() bypasses stale (allowStale) shared-cache reads.
      this._cachesDirty = false;
      this._activeFilter = "default";
      this._newlyRestockedIds = new Set();
      this._currentAvailability = {};
      this._listsCache = null;
      this._listsCacheAt = 0;
      this._listsCacheIncludesProductData = false;
      this._listsCacheIncludesItems = false;
      this._listsRequest = null;
      this._listsRequestIncludesProductData = false;
      this._listsRequestIncludesItems = false;
      this._primaryWishlistCache = null;
      this._primaryWishlistCacheAt = 0;
      this._primaryWishlistRequest = null;
      this._primaryWishlistPageCache = new Map();
      this._primaryWishlistPageRequests = new Map();
      this._wishlistNextCursor = null;
      this._wishlistHasMore = false;
      this._wishlistTotalCount = 0;
      this._availabilityCache = null;
      this._availabilityCacheAt = 0;
      this._availabilityRequest = null;
      this._authoritativeAvailabilityIds = new Set();
      this._listItemsCache = new Map();
      this._listItemsRequests = new Map();
      this._loadToken = 0;
      this._visibleCount = DRAWER_PAGE_SIZE;
      this._paginationObserver = null;
      this._inStockFilterState = "idle";
      this._inStockEligibleIds = new Set();
      this._quantitySaveState = new Map();
      this._restockValidationPending = false;

      try {
        const saved = localStorage.getItem("wl_drawer_filter");
        if (saved) this._activeFilter = saved;
      } catch (e) {}
    }

    init() {
      if (!this.drawer) return;

      Toast.init();
      if ("inert" in this.drawer) {
        this.drawer.inert = true;
      }

      this.drawer.querySelectorAll("[data-wl-view-all], .wl-drawer__view-all").forEach((el) => {
        el.addEventListener("click", (event) => {
          event.preventDefault();
          void this._openFullWishlistModal(el);
        });
      });

      this.drawer.querySelectorAll("[data-wl-close]").forEach((el) => {
        el.addEventListener("click", () => this.close());
      });

      document.addEventListener("click", (event) => {
        const openBtn = event.target.closest("[data-wishlist-drawer-open]");
        if (!openBtn) return;
        event.preventDefault();
        this.open();
      });

      document.addEventListener(WISHLIST_HEADER_CELEBRATE_EVENT, () => {
        if (!this._isCustomerLoggedIn()) {
          return;
        }
        this._celebrateHeaderButtons();
      });

      // Filter button clicks
      this.filtersEl?.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-filter]");
        if (!btn) return;
        this._setFilter(btn.dataset.filter);
      });

      // Clear all button
      this.filtersEl?.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-wl-clear-all]");
        if (!btn) return;
        this._clearAll();
      });

      document.addEventListener("wl:lists:updated", (event) => {
        const detail = event?.detail || null;
        if (!this._isCustomerLoggedIn()) {
          return;
        }
        if (!this._shouldHandleListsUpdatedEvent(detail)) {
          return;
        }
        this._clearListsCache();
        if (this._open) {
          if (detail?.skipDrawerReload === true) {
            return;
          }
          this._load({ force: true });
          return;
        }
        if (this._shouldRefreshClosedCountForListsUpdatedEvent(detail)) {
          this._loadCount();
        }
      });
      document.addEventListener("wl:item:added", (event) => {
        if (!this._isCustomerLoggedIn()) {
          return;
        }
        const detail = event?.detail || {};
        if (detail.source === "wishlist-drawer" && detail.skipDrawerApply === true) {
          return;
        }
        this._clearListItemsCache(detail.listId || this._listId || PRIMARY_WISHLIST_LIST_ID);
        this._applyWishlistItemAddedEvent(detail);
        if (event?.detail?.suppressHeaderCelebrate !== true) {
          this._celebrateHeaderButtons();
        }
      });
      document.addEventListener("wl:item:removed", (event) => {
        if (!this._isCustomerLoggedIn()) {
          return;
        }
        const detail = event?.detail || {};
        if (detail.source === "wishlist-drawer" && detail.skipDrawerApply === true) {
          return;
        }
        this._clearListItemsCache(detail.listId || this._listId || PRIMARY_WISHLIST_LIST_ID);
        this._applyWishlistItemRemovedEvent(detail);
      });
      document.addEventListener(
        "wl:cache:ready",
        () => {
          if (!this._isCustomerLoggedIn()) {
            return;
          }
          const snap = (loadStorage().snapshot || {});
          if (Object.keys(snap).length === 0) this._loadCount();
        },
        { once: true },
      );

      this._loadCount();

      // Apply saved filter active class
      if (this._activeFilter && this._activeFilter !== "default") {
        this.filtersEl?.querySelectorAll("[data-filter]").forEach((btn) => {
          btn.classList.toggle(
            "active",
            btn.dataset.filter === this._activeFilter,
          );
        });
      }
    }

    _shouldHandleListsUpdatedEvent(detail) {
      if (!detail || typeof detail !== "object") {
        return true;
      }

      const domain = String(detail.domain || detail.revisionDomain || "").trim();
      if (domain && domain !== "lists") {
        return false;
      }

      const module = String(detail.module || "").trim();
      if (!module) {
        return true;
      }

      return (
        module === "wishlist:primary-items" ||
        module.startsWith("wishlist:lists:") ||
        module.startsWith("wishlist:list-items:")
      );
    }

    _shouldRefreshClosedCountForListsUpdatedEvent(detail) {
      if (!detail || typeof detail !== "object") {
        return false;
      }

      const source = String(detail.source || "").trim();
      if (source === "remote" || source === "manifest") {
        return false;
      }

      return detail.refreshClosedCount === true;
    }

    _clearListsCache() {
      this._listsCache = null;
      this._listsCacheAt = 0;
      this._listsCacheIncludesProductData = false;
      this._listsCacheIncludesItems = false;
      this._listsRequest = null;
      this._listsRequestIncludesProductData = false;
      this._listsRequestIncludesItems = false;
      this._primaryWishlistCache = null;
      this._primaryWishlistCacheAt = 0;
      this._primaryWishlistRequest = null;
      this._primaryWishlistPageCache.clear();
      this._primaryWishlistPageRequests.clear();
      this._wishlistNextCursor = null;
      this._wishlistHasMore = false;
      this._wishlistTotalCount = 0;
      this._availabilityCache = null;
      this._availabilityCacheAt = 0;
      this._authoritativeAvailabilityIds.clear();
      this._listItemsCache.clear();
      this._listItemsRequests.clear();
      this._inStockFilterState = "idle";
      this._inStockEligibleIds.clear();
      this._cachesDirty = true;
    }

    async _fetchListsData(options = {}) {
      const {
        includeItems = false,
        includeProductData = false,
        force = false,
      } = options;
      const now = Date.now();

      if (
        !force &&
        this._listsCache &&
        now - this._listsCacheAt < LISTS_CACHE_TTL_MS &&
        (!includeItems || this._listsCacheIncludesItems) &&
        (!includeProductData || this._listsCacheIncludesProductData)
      ) {
        return this._listsCache;
      }

      if (
        !force &&
        this._listsRequest &&
        (!includeItems || this._listsRequestIncludesItems) &&
        (!includeProductData || this._listsRequestIncludesProductData)
      ) {
        return this._listsRequest;
      }

      const request = (async () => {
        const sharedData = await querySharedListsCache({
          includeItems,
          includeProductData,
          force,
        });
        if (sharedData) {
          return sharedData;
        }
        return fetchJSON(buildListsUrl({ includeItems, includeProductData }));
      })()
        .then((data) => {
          this._listsCache = data;
          this._listsCacheAt = Date.now();
          this._listsCacheIncludesItems = includeItems;
          this._listsCacheIncludesProductData = includeProductData;
          return data;
        })
        .finally(() => {
          this._listsRequest = null;
          this._listsRequestIncludesItems = false;
          this._listsRequestIncludesProductData = false;
        });

      this._listsRequest = request;
      this._listsRequestIncludesItems = includeItems;
      this._listsRequestIncludesProductData = includeProductData;
      return request;
    }

    _clearListItemsCache(listId) {
      const normalizedListId = String(listId || "").trim();
      if (!normalizedListId) return;
      const listIdsToClear = new Set([normalizedListId]);
      const currentListId = String(this._listId || "").trim();
      if (normalizedListId === PRIMARY_WISHLIST_LIST_ID && currentListId) {
        listIdsToClear.add(currentListId);
      }
      if (currentListId && normalizedListId === currentListId) {
        listIdsToClear.add(PRIMARY_WISHLIST_LIST_ID);
      }
      this._primaryWishlistCache = null;
      this._primaryWishlistCacheAt = 0;
      this._primaryWishlistRequest = null;
      this._primaryWishlistPageCache.clear();
      this._primaryWishlistPageRequests.clear();
      this._wishlistNextCursor = null;
      this._wishlistHasMore = false;
      this._wishlistTotalCount = 0;
      this._availabilityCache = null;
      this._availabilityCacheAt = 0;
      this._authoritativeAvailabilityIds.clear();
      listIdsToClear.forEach((targetListId) => {
        this._listItemsCache.delete(targetListId);
        this._listItemsRequests.delete(targetListId);
      });
      this._inStockFilterState = "idle";
      this._inStockEligibleIds.clear();
      this._cachesDirty = true;
    }

    async _fetchListItems(listId, options = {}) {
      const { force = false, allowStale = true } = options;
      const normalizedListId = String(listId || "").trim();
      if (!normalizedListId) {
        return { items: [] };
      }

      const now = Date.now();
      const cached = this._listItemsCache.get(normalizedListId);
      if (allowStale && !force && cached && now - cached.savedAt < LIST_ITEMS_CACHE_TTL_MS) {
        return cached.data;
      }

      if (allowStale && !force && this._listItemsRequests.has(normalizedListId)) {
        return this._listItemsRequests.get(normalizedListId);
      }

      const request = (async () => {
        const sharedData = await querySharedListItemsCache(normalizedListId, {
          force,
          allowStale,
        });
        if (sharedData) {
          return sharedData;
        }
        return fetchJSON(buildListItemsUrl(normalizedListId));
      })()
        .then((data) => {
          this._listItemsCache.set(normalizedListId, {
            data,
            savedAt: Date.now(),
          });
          return data;
        })
        .finally(() => {
          this._listItemsRequests.delete(normalizedListId);
        });

      this._listItemsRequests.set(normalizedListId, request);
      return request;
    }

    async _fetchPrimaryWishlistItems(options = {}) {
      const {
        force = false,
        allowStale = true,
        cursor = "",
        limit = DRAWER_PAGE_SIZE,
      } = options;
      const now = Date.now();
      const normalizedCursor = String(cursor || "").trim();
      const parsedLimit = Number.parseInt(limit, 10);
      const normalizedLimit = Number.isFinite(parsedLimit) && parsedLimit > 0
        ? Math.max(1, Math.min(100, parsedLimit))
        : DRAWER_PAGE_SIZE;
      const pageKey = `${normalizedLimit}:${normalizedCursor || "first"}`;
      const cachedPage = this._primaryWishlistPageCache.get(pageKey);

      if (
        allowStale &&
        !force &&
        cachedPage?.data &&
        now - cachedPage.savedAt < LIST_ITEMS_CACHE_TTL_MS
      ) {
        return cachedPage.data;
      }

      if (allowStale && !force && this._primaryWishlistPageRequests.has(pageKey)) {
        return this._primaryWishlistPageRequests.get(pageKey);
      }

      const request = (async () => {
        const sharedData = await querySharedPrimaryWishlistItemsCache({
          force,
          allowStale,
          cursor: normalizedCursor,
          limit: normalizedLimit,
        });
        if (sharedData) {
          return sharedData;
        }
        return fetchJSON(buildPrimaryWishlistItemsUrl({
          cursor: normalizedCursor,
          limit: normalizedLimit,
        }));
      })()
        .then((data) => {
          this._primaryWishlistCache = data;
          this._primaryWishlistCacheAt = Date.now();
          this._primaryWishlistPageCache.set(pageKey, {
            data,
            savedAt: Date.now(),
          });
          return data;
        })
        .finally(() => {
          this._primaryWishlistPageRequests.delete(pageKey);
          if (!normalizedCursor) {
            this._primaryWishlistRequest = null;
          }
        });

      this._primaryWishlistPageRequests.set(pageKey, request);
      if (!normalizedCursor) {
        this._primaryWishlistRequest = request;
      }
      return request;
    }

    async _fetchPrimaryWishlistAvailability(options = {}) {
      const { force = false, allowStale = true } = options;
      const now = Date.now();

      if (
        !force &&
        this._availabilityCache &&
        now - this._availabilityCacheAt < PRIMARY_WISHLIST_AVAILABILITY_TTL_MS
      ) {
        return this._availabilityCache;
      }

      if (this._availabilityRequest) {
        return this._availabilityRequest;
      }

      const request = (async () => {
        const sharedData = await querySharedPrimaryWishlistAvailabilityCache({
          force,
          allowStale,
        });
        if (sharedData) {
          return sharedData;
        }
        return fetchJSON(buildPrimaryWishlistAvailabilityUrl());
      })()
        .then((data) => {
          this._availabilityCache = data;
          this._availabilityCacheAt = Date.now();
          return data;
        })
        .finally(() => {
          this._availabilityRequest = null;
        });

      this._availabilityRequest = request;
      return request;
    }

    _normalizeItems(items) {
      return dedupeWishlistItems(items);
    }

    _applyStoredQuantities(items, listId) {
      const normalizedListId = String(listId || PRIMARY_WISHLIST_LIST_ID).trim();
      return (Array.isArray(items) ? items : []).map((rawItem) => {
        const serverProvidedQuantity = hasExplicitWishlistItemQuantity(rawItem);
        const item = normalizeWishlistItem(rawItem);
        const itemId = String(item?.id || item?.itemId || "").trim();
        if (!itemId) return item;

        if (serverProvidedQuantity) {
          clearStoredWishlistQuantity(normalizedListId, itemId);
          return item;
        }

        const storedQuantity = getStoredWishlistQuantity(normalizedListId, itemId);
        return storedQuantity
          ? { ...item, quantity: storedQuantity }
          : item;
      });
    }

    async _fetchPrimaryWishlistData(options = {}) {
      const {
        force = false,
        allowDetailedFallback = true,
        allowStale = true,
        cursor = "",
        limit = DRAWER_PAGE_SIZE,
      } = options;
      if (!this._isCustomerLoggedIn()) {
        return { wishlistList: null, items: [], nextCursor: null, hasMore: false, totalCount: 0 };
      }

      try {
        const primaryPayload = await this._fetchPrimaryWishlistItems({
          force,
          allowStale,
          cursor,
          limit,
        });
        const list =
          primaryPayload?.list && typeof primaryPayload.list === "object"
            ? primaryPayload.list
            : {
                id: PRIMARY_WISHLIST_LIST_ID,
                name: "Wishlist",
                specialType: "wishlist",
                isDefault: true,
              };
        const items = dedupeWishlistItems(
          this._applyStoredQuantities(primaryPayload?.items, list.id),
        );
        const hasMore = primaryPayload?.hasMore === true;
        return {
          wishlistList: list,
          items,
          nextCursor: primaryPayload?.nextCursor || primaryPayload?.cursor || null,
          hasMore,
          totalCount:
            !hasMore
              ? items.length
              : Number.isFinite(Number(primaryPayload?.totalCount))
              ? Number(primaryPayload.totalCount)
              : Number.isFinite(Number(list?.itemsCount))
                ? Number(list.itemsCount)
                : undefined,
        };
      } catch (error) {
        if (!allowDetailedFallback) {
          throw error;
        }
        console.warn("[WishlistDrawer] primary wishlist items fetch failed", error);
      }

      let data = await this._fetchListsData({
        includeItems: false,
        includeProductData: false,
        force,
      });
      let lists = data?.lists ?? [];
      let wishlistList = findPrimaryWishlistList(lists);

      if (wishlistList?.id) {
        try {
          const itemPayload = await this._fetchListItems(wishlistList.id, {
            force,
            allowStale,
          });
          const items = dedupeWishlistItems(
            this._applyStoredQuantities(itemPayload?.items, wishlistList.id),
          );
          const hasMore = itemPayload?.hasMore === true;
          return {
            wishlistList,
            items,
            nextCursor: itemPayload?.nextCursor || itemPayload?.cursor || null,
            hasMore,
            totalCount:
              !hasMore
                ? items.length
                : Number.isFinite(Number(itemPayload?.totalCount))
                ? Number(itemPayload.totalCount)
                : undefined,
          };
        } catch (error) {
          console.warn("[WishlistDrawer] list items fetch failed", error);
          if (Array.isArray(wishlistList.items)) {
            const items = dedupeWishlistItems(
              this._applyStoredQuantities(wishlistList.items, wishlistList.id),
            );
            return {
              wishlistList,
              items,
              nextCursor: null,
              hasMore: false,
              totalCount: items.length,
            };
          }
          if (!allowDetailedFallback) {
            throw error;
          }
        }
      }

      if (!allowDetailedFallback) {
        return { wishlistList, items: [], nextCursor: null, hasMore: false, totalCount: 0 };
      }

      data = await this._fetchListsData({
        includeItems: true,
        includeProductData: false,
        force: true,
      });
      lists = data?.lists ?? [];
      wishlistList = findPrimaryWishlistList(lists);
      const items = dedupeWishlistItems(
        this._applyStoredQuantities(wishlistList?.items, wishlistList?.id),
      );

      return {
        wishlistList,
        items,
        nextCursor: null,
        hasMore: false,
        totalCount: items.length,
      };
    }

    _hasResolvedAvailabilityForItem(item) {
      const itemId = String(item?.id || "").trim();
      if (itemId && this._authoritativeAvailabilityIds.has(itemId)) {
        return true;
      }

      const product = getResolvedProduct(item);
      if (product && Array.isArray(product.variants)) {
        return true;
      }

      const handle = String(item?.handle || "").trim();
      return handle ? isMissingProductHandle(handle) : true;
    }

    _hasAuthoritativeAvailabilityForItem(item) {
      const itemId = String(item?.id || "").trim();
      return itemId ? this._authoritativeAvailabilityIds.has(itemId) : false;
    }

    _computeInStockEligibleIds() {
      const eligibleIds = new Set();
      for (const item of this._items) {
        const product = getResolvedProduct(item);
        const variants = getResolvedVariants(product);
        if (variants.length === 0) continue;
        const variant = getMatchingVariant(product, item.variantId);
        if (isVariantAvailable(variant)) {
          eligibleIds.add(item.id);
        }
      }
      return eligibleIds;
    }

    _applyItemAvailabilityMap(itemAvailability) {
      const eligibleIds = new Set();
      this._authoritativeAvailabilityIds.clear();
      if (!itemAvailability || typeof itemAvailability !== "object") {
        return eligibleIds;
      }

      for (const item of this._items) {
        const itemId = String(item?.id || "").trim();
        if (!itemId) continue;

        if (itemAvailability[itemId] === true) {
          eligibleIds.add(itemId);
        }
        if (typeof itemAvailability[itemId] === "boolean") {
          this._authoritativeAvailabilityIds.add(itemId);
          this._currentAvailability[itemId] = itemAvailability[itemId];
        }
      }

      return eligibleIds;
    }

    _applyAvailabilityPayload(availabilityPayload) {
      const itemAvailability =
        availabilityPayload?.itemAvailability &&
        typeof availabilityPayload.itemAvailability === "object"
          ? availabilityPayload.itemAvailability
          : null;

      if (!itemAvailability) {
        return null;
      }

      return this._applyItemAvailabilityMap(itemAvailability);
    }

    async _refreshAvailabilitySnapshot(loadToken, options = {}) {
      try {
        const availabilityPayload = await this._fetchPrimaryWishlistAvailability({
          ...options,
          allowStale: false,
        });
        if (loadToken !== this._loadToken || !this._open) {
          return;
        }

        const eligibleIds =
          this._applyAvailabilityPayload(availabilityPayload) || new Set();

        if (this._activeFilter === "instock" && this._inStockFilterState === "ready") {
          this._inStockEligibleIds = eligibleIds;
        }

        this._recomputeRestockState();
        await this._prefetchProductImagesForItems(
          this._items.filter((item) => this._newlyRestockedIds.has(item.id)),
          loadToken,
        );
        if (loadToken !== this._loadToken || !this._open) {
          return;
        }
        this._renderSorted();
      } catch (error) {
        console.warn("[WishlistDrawer] availability snapshot refresh failed", error);
      }
    }

    async _ensureAvailabilityForInStockFilter(loadToken) {
      if (this._activeFilter !== "instock") return;
      if (this._inStockFilterState === "loading") return;
      if (!this._items.length) {
        this._inStockFilterState = "ready";
        this._inStockEligibleIds.clear();
        return;
      }

      this._inStockFilterState = "loading";
      this._renderSorted();

      try {
        const availabilityPayload = await this._fetchPrimaryWishlistAvailability({
          allowStale: false,
        });
        this._inStockEligibleIds =
          this._applyAvailabilityPayload(availabilityPayload) || new Set();
      } catch (error) {
        console.warn("[WishlistDrawer] in-stock availability batch failed", error);
        this._inStockEligibleIds = this._computeInStockEligibleIds();
      }

      if (loadToken !== this._loadToken || !this._open) {
        return;
      }

      this._inStockFilterState = "ready";
      this._recomputeRestockState();
      this._renderSorted();
      this._refreshVisibleMissingProducts(loadToken);
    }

    _recomputeAvailabilityState() {
      this._currentAvailability = {};
      for (const item of this._items) {
        const product = getResolvedProduct(item);
        const variants = getResolvedVariants(product);
        const variant = getMatchingVariant(product, item.variantId);
        const hasSavedVariantFallback =
          variants.length === 0 && Boolean(extractResourceId(item.variantId));
        const missingProduct = item.handle ? isMissingProductHandle(item.handle) : false;
        this._currentAvailability[item.id] = hasSavedVariantFallback
          ? !missingProduct
          : isVariantAvailable(variant);
      }
    }

    _recomputeRestockState() {
      const state = loadStorage();
      const snapshot = state.snapshot || {};
      const persistedRestockedIds = Array.isArray(state.newlyRestockedIds)
        ? state.newlyRestockedIds
            .map((id) => String(id || "").trim())
            .filter(Boolean)
        : [];
      this._newlyRestockedIds.clear();

      for (const item of this._items) {
        const itemId = String(item?.id || "").trim();
        if (!itemId) continue;
        const snap = snapshot[item.id];
        const hasAuthoritativeAvailability = this._hasAuthoritativeAvailabilityForItem(item);
        const nowAvailable = hasAuthoritativeAvailability
          ? this._currentAvailability[item.id] === true
          : null;
        // Embedded wishlist product snapshots can be stale; only the batched
        // availability payload may expand the restock notice set.
        const wasOos = snap !== undefined && snap.available === false;
        const wasPersistedRestock = persistedRestockedIds.includes(itemId);

        if (wasOos && nowAvailable === true) {
          this._newlyRestockedIds.add(itemId);
        } else if (wasPersistedRestock && nowAvailable === true) {
          this._newlyRestockedIds.add(itemId);
        }
      }

      this._saveRestockState();
    }

    async _prefetchProductImagesForItems(items, loadToken, options = {}) {
      const handles = [
        ...new Set(
          (Array.isArray(items) ? items : [])
            .filter((item) => itemNeedsProductImageRefresh(item))
            .map((item) => String(item?.handle || "").trim())
            .filter(Boolean),
        ),
      ];

      if (!handles.length) {
        return;
      }

      try {
        await prefetchProductsForHandles(handles, null, options);
      } catch (error) {
        console.warn("[WishlistDrawer] restock image prefetch failed", error);
      }

      if (loadToken !== this._loadToken || !this._open) {
        return;
      }
    }

    async _refreshProducts(handles, loadToken, options = {}) {
      const productHandles = [...new Set((handles || []).filter(Boolean))];
      if (!productHandles.length) return;

      try {
        await prefetchProductsForHandles(productHandles, null, options);

        if (loadToken !== this._loadToken || !this._open) {
          return;
        }

        this._recomputeAvailabilityState();
        this._applyAvailabilityPayload(this._availabilityCache);
        if (this._activeFilter === "instock" && this._inStockFilterState === "ready") {
          const itemAvailability = this._availabilityCache?.itemAvailability;
          if (itemAvailability && typeof itemAvailability === "object") {
            this._inStockEligibleIds = new Set(
              this._items
                .map((item) => String(item?.id || "").trim())
                .filter((itemId) => itemId && itemAvailability[itemId] === true),
            );
          } else {
            this._inStockEligibleIds = this._computeInStockEligibleIds();
          }
        }
        this._recomputeRestockState();
        this._renderSorted();
      } catch (error) {
        console.warn("[WishlistDrawer] product refresh failed", error);
      }
    }

    _getPersistedRestockIds() {
      const activeItemIds = new Set(
        this._items.map((item) => String(item?.id || "")).filter(Boolean),
      );
      return [...this._newlyRestockedIds].filter((id) =>
        activeItemIds.has(String(id)),
      );
    }

    _saveRestockState(extraState = {}, options = {}) {
      const state = loadStorage();
      const newlyRestockedIds =
        options.preserveRestockedIds === true &&
        Array.isArray(state.newlyRestockedIds)
          ? state.newlyRestockedIds
              .map((id) => String(id || "").trim())
              .filter(Boolean)
          : this._getPersistedRestockIds();
      saveStorage({ ...state, ...extraState, newlyRestockedIds });
      this._updateHeaderCount(newlyRestockedIds.length);
      return newlyRestockedIds;
    }

    _setFilter(filter) {
      this._activeFilter = filter;
      this._visibleCount = DRAWER_PAGE_SIZE;
      if (filter !== "instock") {
        this._inStockFilterState = "idle";
      }
      try {
        localStorage.setItem("wl_drawer_filter", filter);
      } catch (e) {}
      this.filtersEl?.querySelectorAll("[data-filter]").forEach((btn) => {
        btn.classList.toggle("active", btn.dataset.filter === filter);
      });
      this._renderSorted();
      if (filter === "instock") {
        void this._ensureAvailabilityForInStockFilter(this._loadToken);
      }
      this._refreshVisibleMissingProducts(this._loadToken);
    }

    open() {
      if (!this.drawer || this._open) return;
      this._lastFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      this._open = true;
      this.drawer.setAttribute("aria-hidden", "false");
      if ("inert" in this.drawer) {
        this.drawer.inert = false;
      }
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", this._boundHandleKeydown);
      window.requestAnimationFrame(() => {
        const firstFocusable =
          this.panelEl?.querySelector(".wl-drawer__close") ||
          this.panelEl?.querySelector(FOCUSABLE_SELECTOR);
        firstFocusable?.focus?.({ preventScroll: true });
      });
      if (!this._isCustomerLoggedIn()) {
        this._showSignedOutState();
        return;
      }
      this._load();
    }

    close() {
      if (!this.drawer || !this._open) return;
      this._open = false;
      this._loadToken += 1;
      this._disconnectPaginationObserver();
      this.drawer.setAttribute("aria-hidden", "true");
      if ("inert" in this.drawer) {
        this.drawer.inert = true;
      }
      document.body.style.overflow = "";
      document.removeEventListener("keydown", this._boundHandleKeydown);

      const state = loadStorage();
      let nextSnapshot = state.snapshot || {};
      const preservePendingRestockState = this._restockValidationPending;

      // Save the latest live availability snapshot so future restock checks
      // compare against the storefront, not embedded wishlist payloads.
      if (
        !preservePendingRestockState &&
        Object.keys(this._currentAvailability).length > 0
      ) {
        nextSnapshot = { ...nextSnapshot };

        for (const item of this._items) {
          if (!this._hasResolvedAvailabilityForItem(item)) {
            if (!nextSnapshot[item.id]) {
              nextSnapshot[item.id] = {
                available: true,
                handle: item.handle,
              };
            }
            continue;
          }

          nextSnapshot[item.id] = {
            available: this._currentAvailability[item.id] !== false,
            handle: item.handle,
          };
        }

        // Remove stale entries (items no longer in wishlist)
        for (const id of Object.keys(nextSnapshot)) {
          if (!this._items.some((i) => i.id === id)) {
            delete nextSnapshot[id];
          }
        }
      }

      this._saveRestockState(
        { snapshot: nextSnapshot },
        { preserveRestockedIds: preservePendingRestockState },
      );
      this._restockValidationPending = false;
      this._lastFocusedElement?.focus?.({ preventScroll: true });
    }

    async _openFullWishlistModal(triggerElement) {
      const fallbackUrl =
        triggerElement instanceof HTMLAnchorElement && triggerElement.href
          ? triggerElement.href
          : "/account?tab=garden&garden-open-wishlist-list=1";

      this.close();

      if (!this._isCustomerLoggedIn()) {
        window.location.href = buildWishlistLoginUrl(fallbackUrl);
        return;
      }

      try {
        const openMyGardenWishlistModal =
          window.MrMapleMyPlants?.openWishlistListModal;
        if (typeof openMyGardenWishlistModal === "function") {
          const opened = await openMyGardenWishlistModal({
            defaultListName: "Wishlist",
            source: "wishlist-drawer",
            triggerElement,
            force: true,
          });
          if (opened) {
            return;
          }
        }

        const loadWishlistApp = window.__mmWishlistAppLoader?.load;
        if (!window.MrMapleWishlist?.openListModal && typeof loadWishlistApp === "function") {
          await loadWishlistApp();
        }

        const openListModal =
          window.MrMapleWishlist?.openListModal ||
          window.__mmOpenWishlistListModal;

        if (typeof openListModal !== "function") {
          if (typeof window.MrMapleAccountTabs?.activate === "function") {
            window.MrMapleAccountTabs.activate("wishlist");
            return;
          }
          window.location.href = fallbackUrl;
          return;
        }

        const opened = await openListModal({
          defaultListName: "Wishlist",
          source: "wishlist-drawer",
          triggerElement,
          force: true,
        });

        if (!opened) {
          if (typeof window.MrMapleAccountTabs?.activate === "function") {
            window.MrMapleAccountTabs.activate("wishlist");
            return;
          }
          window.location.href = fallbackUrl;
        }
      } catch (error) {
        console.warn("[WishlistDrawer] full wishlist modal failed:", error);
        if (typeof window.MrMapleAccountTabs?.activate === "function") {
          window.MrMapleAccountTabs.activate("wishlist");
          return;
        }
        window.location.href = fallbackUrl;
      }
    }

    _handleKeydown(event) {
      if (!this._open) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== "Tab" || !this.panelEl) return;

      const focusable = Array.from(
        this.panelEl.querySelectorAll(FOCUSABLE_SELECTOR),
      ).filter((element) => !element.hasAttribute("hidden"));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _setSectionVisibility(element, isVisible) {
      if (!element) return;
      element.hidden = !isVisible;
      element.setAttribute("aria-hidden", isVisible ? "false" : "true");
    }

    _showState(state) {
      if (state === "empty" && this._isCustomerLoggedIn()) {
        this._restoreEmptyStateCopy();
      }

      this._setSectionVisibility(this.loadingEl, state === "loading");
      this._setSectionVisibility(this.emptyEl, state === "empty");

      const showItems = state === "items";
      this._setSectionVisibility(this.footerEl, showItems);
      this._setSectionVisibility(this.filtersEl, showItems);
      if (this.itemsEl) {
        this.itemsEl.hidden = !showItems;
        this.itemsEl.style.display = showItems ? "" : "none";
      }
    }

    _restoreEmptyStateCopy() {
      if (this.emptyTextEl) {
        this.emptyTextEl.textContent = this._defaultEmptyText;
      }
      if (this.emptyActionEl) {
        this.emptyActionEl.textContent = this._defaultEmptyActionText;
        this.emptyActionEl.setAttribute("href", this._defaultEmptyActionHref);
      }
    }

    _showSignedOutState() {
      this._items = [];
      this._listId = null;
      this._updateCount(0);
      this._updateHeaderCount(0);
      if (this.emptyTextEl) {
        this.emptyTextEl.textContent = "Sign in to view your wishlist";
      }
      if (this.emptyActionEl) {
        this.emptyActionEl.textContent = "Sign In";
        this.emptyActionEl.setAttribute("href", buildWishlistLoginUrl());
      }
      this._showState("empty");
    }

    _shouldApplyWishlistMutationForList(listId) {
      const normalizedListId = String(listId || PRIMARY_WISHLIST_LIST_ID).trim();
      if (!normalizedListId) return false;
      if (!this._listId) return true;
      return (
        normalizedListId === PRIMARY_WISHLIST_LIST_ID ||
        String(this._listId) === normalizedListId
      );
    }

    _hasPendingWishlistMutations() {
      return this._pendingWishlistAdds.length > 0 || this._pendingWishlistRemoves.length > 0;
    }

    _mutationEntryMatchesList(entry, listId) {
      const targetListId = String(listId || this._listId || PRIMARY_WISHLIST_LIST_ID).trim();
      const entryListId = String(entry?.listId || PRIMARY_WISHLIST_LIST_ID).trim();
      return (
        !targetListId ||
        !entryListId ||
        targetListId === PRIMARY_WISHLIST_LIST_ID ||
        entryListId === PRIMARY_WISHLIST_LIST_ID ||
        entryListId === targetListId
      );
    }

    _upsertPendingWishlistAdd(listId, item) {
      if (!item || typeof item !== "object") return;
      const entry = { listId, item };
      this._pendingWishlistRemoves = this._pendingWishlistRemoves.filter(
        (candidate) => !wishlistItemsMatchIdentity(item, candidate.detail || candidate),
      );
      const existingIndex = this._pendingWishlistAdds.findIndex((candidate) =>
        wishlistItemsMatchIdentity(candidate.item, item),
      );
      if (existingIndex >= 0) {
        this._pendingWishlistAdds[existingIndex] = entry;
      } else {
        this._pendingWishlistAdds.push(entry);
      }
      if (this._pendingWishlistAdds.length > 100) {
        this._pendingWishlistAdds = this._pendingWishlistAdds.slice(-100);
      }
    }

    _recordPendingWishlistRemove(listId, detail) {
      const removeDetail = { ...detail, listId };
      this._pendingWishlistAdds = this._pendingWishlistAdds.filter(
        (entry) => !wishlistItemsMatchIdentity(entry.item, removeDetail),
      );
      const existingIndex = this._pendingWishlistRemoves.findIndex((entry) =>
        wishlistItemsMatchIdentity(entry.detail || entry, removeDetail),
      );
      const entry = { listId, detail: removeDetail };
      if (existingIndex >= 0) {
        this._pendingWishlistRemoves[existingIndex] = entry;
      } else {
        this._pendingWishlistRemoves.push(entry);
      }
      if (this._pendingWishlistRemoves.length > 100) {
        this._pendingWishlistRemoves = this._pendingWishlistRemoves.slice(-100);
      }
    }

    _clearPendingWishlistRemove(listId, detail) {
      const removeDetail = { ...detail, listId };
      this._pendingWishlistRemoves = this._pendingWishlistRemoves.filter(
        (entry) => !wishlistItemsMatchIdentity(entry.detail || entry, removeDetail),
      );
    }

    _mergePendingWishlistMutations(items, listId, totalCount) {
      let nextItems = Array.isArray(items)
        ? dedupeWishlistItems(items)
        : [];
      const originalLength = nextItems.length;
      const removes = this._pendingWishlistRemoves.filter((entry) =>
        this._mutationEntryMatchesList(entry, listId),
      );
      const adds = this._pendingWishlistAdds.filter((entry) =>
        this._mutationEntryMatchesList(entry, listId),
      );

      if (removes.length) {
        nextItems = nextItems.filter(
          (item) => !removes.some((entry) => wishlistItemsMatchIdentity(item, entry.detail || entry)),
        );
      }

      const removedFetchedCount = Math.max(0, originalLength - nextItems.length);
      let addedMissingCount = 0;
      for (const entry of adds) {
        const item = normalizeWishlistItem({
          ...(entry.item || {}),
          listId: entry.listId || listId,
        });
        if (!item || typeof item !== "object") continue;
        if (removes.some((removeEntry) => wishlistItemsMatchIdentity(item, removeEntry.detail || removeEntry))) {
          continue;
        }
        const existingIndex = nextItems.findIndex((candidate) =>
          wishlistItemsMatchIdentity(candidate, item),
        );
        if (existingIndex >= 0) {
          nextItems[existingIndex] = normalizeWishlistItem({
            ...nextItems[existingIndex],
            ...item,
          });
        } else {
          nextItems.unshift(item);
          addedMissingCount += 1;
        }
      }

      let nextTotal = Number.isFinite(Number(totalCount)) ? Number(totalCount) : originalLength;
      nextTotal = Math.max(0, nextTotal + addedMissingCount - removedFetchedCount);
      nextTotal = Math.max(nextTotal, nextItems.length);
      return { items: nextItems, totalCount: nextTotal };
    }

    _syncWishlistDrawerItemsAfterMutation(options = {}) {
      const explicitTotal = Number(options.totalCount);
      this._wishlistTotalCount = Number.isFinite(explicitTotal)
        ? Math.max(0, explicitTotal)
        : Math.max(0, this._wishlistTotalCount, this._items.length);
      this._visibleCount = Math.max(
        DRAWER_PAGE_SIZE,
        Math.min(Math.max(this._visibleCount, DRAWER_PAGE_SIZE), Math.max(this._items.length, DRAWER_PAGE_SIZE)),
      );
      this._updateCount(this._wishlistTotalCount);
      this._recomputeAvailabilityState();

      if (!this._open) {
        return;
      }

      if (this._items.length === 0) {
        this._showState("empty");
        return;
      }

      this._renderSorted();
      this._showState("items");
      this._refreshVisibleMissingProducts(this._loadToken);
    }

    _applyWishlistItemAddedEvent(detail = {}) {
      const listId = String(detail.listId || this._listId || PRIMARY_WISHLIST_LIST_ID).trim();
      if (!this._shouldApplyWishlistMutationForList(listId)) return;
      if (!detail.item || typeof detail.item !== "object") return;

      const item = normalizeWishlistItem({
        ...detail.item,
        listId,
      });
      if (!item || typeof item !== "object") return;
      if (!getWishlistItemId(item) && !item.productId && !item.productGid) return;

      if (!this._listId) {
        this._listId = listId;
      }
      this._upsertPendingWishlistAdd(listId, item);

      const existingIndex = this._items.findIndex((entry) =>
        wishlistItemsMatchIdentity(entry, { ...detail, item }),
      );
      const nextTotalCount =
        existingIndex >= 0
          ? Math.max(this._wishlistTotalCount, this._items.length)
          : Math.max(this._wishlistTotalCount + 1, this._items.length + 1);

      if (existingIndex >= 0) {
        const current = this._items[existingIndex];
        this._items[existingIndex] = normalizeWishlistItem({
          ...current,
          ...item,
          _optimistic: detail.optimistic === true ? true : item._optimistic,
        });
      } else {
        this._items = [item, ...this._items];
      }

      primeProductCacheFromItems([item]);
      this._syncWishlistDrawerItemsAfterMutation({ totalCount: nextTotalCount });
    }

    _applyWishlistItemRemovedEvent(detail = {}) {
      const listId = String(detail.listId || this._listId || PRIMARY_WISHLIST_LIST_ID).trim();
      if (!this._shouldApplyWishlistMutationForList(listId)) return;
      this._recordPendingWishlistRemove(listId, detail);
      if (!this._items.length) return;

      const previousLength = this._items.length;
      this._items = this._items.filter((item) => !wishlistItemsMatchIdentity(item, detail));
      if (this._items.length === previousLength) return;

      this._newlyRestockedIds.forEach((itemId) => {
        const stillPresent = this._items.some(
          (item) => String(item?.id || item?.itemId || "").trim() === itemId,
        );
        if (!stillPresent) {
          this._newlyRestockedIds.delete(itemId);
        }
      });
      this._saveRestockState();
      const removedCount = Math.max(0, previousLength - this._items.length);
      this._syncWishlistDrawerItemsAfterMutation({
        totalCount: Math.max(0, this._wishlistTotalCount - removedCount, this._items.length),
      });
    }

    // Background load: validates persisted restock notices against the primary
    // wishlist and batched availability endpoints. It avoids all-list product
    // hydration and never shows stale localStorage counts before validation.
    async _loadCount(options = {}) {
      try {
        const { force = false } = options;

        if (!this._isCustomerLoggedIn()) return;

        const state = loadStorage();
        const snapshot = state.snapshot || {};
        const persistedRestockedIds = Array.isArray(state.newlyRestockedIds)
          ? [
              ...new Set(
                state.newlyRestockedIds
                  .map((id) => String(id || "").trim())
                  .filter(Boolean),
              ),
            ]
          : [];
        const hasSnapshot = Object.keys(snapshot).length > 0;
        const hasPersistedRestockIds = persistedRestockedIds.length > 0;
        const hasTrackedOos = hasTrackedOosItems(snapshot);

        if (hasPersistedRestockIds) {
          this._updateHeaderCount(0);
        }

        if (!hasSnapshot && !hasPersistedRestockIds) {
          return;
        }

        // Only skip the fetch when we have an established snapshot with no OOS
        // items and no persisted restock IDs — nothing to check.
        if (!force && !hasPersistedRestockIds && hasSnapshot && !hasTrackedOos) {
          return;
        }

        const shouldForceValidation = force || hasPersistedRestockIds || hasTrackedOos;
        const { items } = await this._fetchPrimaryWishlistData({
          force: shouldForceValidation,
          allowDetailedFallback: false,
          allowStale: false,
        });

        if (items.length === 0) {
          if (hasPersistedRestockIds) {
            saveStorage({ ...state, newlyRestockedIds: [] });
          }
          this._updateHeaderCount(0);
          return;
        }

        const activeItemIds = new Set(
          items.map((item) => String(item?.id || "").trim()).filter(Boolean),
        );

        // Items that were OOS last time the snapshot was saved
        const wasOosItems = items.filter((item) => {
          const snap = snapshot[item.id];
          return snap !== undefined && snap.available === false;
        });

        if (wasOosItems.length === 0 && !hasPersistedRestockIds) {
          this._updateHeaderCount(0);
          return;
        }

        const availabilityPayload = await this._fetchPrimaryWishlistAvailability({
          force: shouldForceValidation,
          allowStale: false,
        });
        if (availabilityPayload?.availabilityUnavailable === true) {
          this._updateHeaderCount(0);
          return;
        }

        const itemAvailability =
          availabilityPayload?.itemAvailability &&
          typeof availabilityPayload.itemAvailability === "object"
            ? availabilityPayload.itemAvailability
            : null;
        if (!itemAvailability) {
          this._updateHeaderCount(0);
          return;
        }

        const validIds = [];
        const appendValidId = (id) => {
          const normalizedId = String(id || "").trim();
          if (
            normalizedId &&
            activeItemIds.has(normalizedId) &&
            itemAvailability[normalizedId] === true &&
            !validIds.includes(normalizedId)
          ) {
            validIds.push(normalizedId);
          }
        };

        persistedRestockedIds.forEach(appendValidId);
        wasOosItems.forEach((item) => appendValidId(item.id));

        if (
          validIds.length !== persistedRestockedIds.length ||
          validIds.some((id, index) => id !== persistedRestockedIds[index])
        ) {
          saveStorage({ ...state, newlyRestockedIds: validIds });
        }
        this._updateHeaderCount(validIds.length);
      } catch {
        // silent
      }
    }

    async _load(options = {}) {
      if (!this._isCustomerLoggedIn()) {
        this._showSignedOutState();
        return;
      }

      let { force = false } = options;
      // A mutation (clear-all, remove, add) invalidated caches; force a fresh
      // server read so we don't render stale allowStale shared-cache items.
      if (this._cachesDirty) {
        force = true;
        this._cachesDirty = false;
      }
      const loadToken = ++this._loadToken;
      this._restockValidationPending = false;
      if (this._hasPendingWishlistMutations() && this._items.length > 0) {
        this._syncWishlistDrawerItemsAfterMutation();
      } else {
        this._showState("loading");
      }
      const persistedState = loadStorage();
      const availabilitySnapshot = persistedState.snapshot || {};
      const persistedRestockedIds = persistedState.newlyRestockedIds || [];
      const shouldPrimeAvailability =
        this._activeFilter === "instock" ||
        hasTrackedOosItems(availabilitySnapshot) ||
        persistedRestockedIds.length > 0;

      let primeAvailability = null;
      if (shouldPrimeAvailability) {
        primeAvailability = this._fetchPrimaryWishlistAvailability({
          force:
            force ||
            hasTrackedOosItems(availabilitySnapshot) ||
            persistedRestockedIds.length > 0,
          allowStale: false,
        });
        this._restockValidationPending = true;
        primeAvailability.catch(() => undefined);
      }

      try {
        let { wishlistList, items, nextCursor, hasMore, totalCount } = await this._fetchPrimaryWishlistData({
          force,
          allowStale: false,
          limit: DRAWER_PAGE_SIZE,
        });
        if (loadToken !== this._loadToken) {
          return;
        }

        if (items.length === 0 && !force && this._isCustomerLoggedIn()) {
          const refreshed = await this._fetchPrimaryWishlistData({
            force: true,
            allowStale: false,
          });
          if (loadToken !== this._loadToken) return;

          wishlistList = refreshed.wishlistList;
          items = refreshed.items;
          nextCursor = refreshed.nextCursor;
          hasMore = refreshed.hasMore;
          totalCount = refreshed.totalCount;
        }

        if (!wishlistList) {
          this._showState("empty");
          this._updateCount(0);
          return;
        }

        this._listId = wishlistList.id;
        const mergedWishlistData = this._mergePendingWishlistMutations(
          items,
          wishlistList.id,
          totalCount,
        );
        this._items = mergedWishlistData.items;
        this._wishlistNextCursor = nextCursor || null;
        this._wishlistHasMore = hasMore === true;
        this._wishlistTotalCount = mergedWishlistData.totalCount;
        this._visibleCount = DRAWER_PAGE_SIZE;
        this._inStockFilterState = "idle";
        this._inStockEligibleIds.clear();
        this._updateCount(this._wishlistTotalCount || this._items.length);

        if (this._items.length === 0) {
          this._showState("empty");
          return;
        }

        primeProductCacheFromItems(this._items);

        const needsAvailabilityRefresh = this._items.some((item) =>
          itemNeedsFreshAvailabilityCheck(
            item,
            availabilitySnapshot,
            persistedRestockedIds,
          ),
        );
        this._recomputeAvailabilityState();

        let appliedEligibility = null;
        if (primeAvailability) {
          try {
            const availabilityPayload = await primeAvailability;
            if (loadToken !== this._loadToken || !this._open) {
              return;
            }
            appliedEligibility = this._applyAvailabilityPayload(availabilityPayload);
          } catch (error) {
            console.warn("[WishlistDrawer] initial availability validation failed", error);
          } finally {
            if (loadToken === this._loadToken) {
              this._restockValidationPending = false;
            }
          }
        }

        if (!appliedEligibility) {
          appliedEligibility = this._applyAvailabilityPayload(this._availabilityCache);
        }

        this._recomputeRestockState();

        if (this._activeFilter === "instock") {
          if (appliedEligibility) {
            this._inStockEligibleIds = appliedEligibility;
            this._inStockFilterState = "ready";
          } else {
            void this._ensureAvailabilityForInStockFilter(loadToken);
          }
        }

        await this._prefetchProductImagesForItems(
          this._items.filter((item) => this._newlyRestockedIds.has(item.id)),
          loadToken,
        );
        if (loadToken !== this._loadToken || !this._open) {
          return;
        }

        this._renderSorted();
        this._refreshVisibleMissingProducts(loadToken);
        if (loadToken !== this._loadToken) {
          return;
        }
        this._showState("items");

        if (
          needsAvailabilityRefresh &&
          !appliedEligibility &&
          this._activeFilter !== "instock"
        ) {
          void this._refreshAvailabilitySnapshot(loadToken, { force: true });
        }
      } catch (err) {
        console.error("[WishlistDrawer] load error", err);
        this._showState("empty");
      }
    }

    _isCustomerLoggedIn() {
      return isWishlistCustomerAuthenticated();
    }

    _updateCount(n) {
      if (this.countEl) {
        this.countEl.textContent = n > 0 ? `(${n})` : "";
      }
    }

    _updateHeaderCount(n) {
      this._getHeaderCountEls().forEach((element) => {
        element.textContent = n > 0 ? n : "";
        element.classList.toggle("visible", n > 0);
      });
    }

    _getHeaderCountEls() {
      return Array.from(document.querySelectorAll("[data-wl-header-count]"));
    }

    _getHeaderButtons() {
      return Array.from(document.querySelectorAll("[data-wishlist-drawer-open]"));
    }

    _celebrateHeaderButtons() {
      this._getHeaderButtons().forEach((button) => {
        const existingTimer = HEADER_ANIMATION_TIMERS.get(button);
        if (existingTimer) {
          window.clearTimeout(existingTimer);
        }

        button.classList.remove("wl-fed");
        void button.offsetWidth;
        button.classList.add("wl-fed");

        const timer = window.setTimeout(() => {
          button.classList.remove("wl-fed");
          HEADER_ANIMATION_TIMERS.delete(button);
        }, 720);

        HEADER_ANIMATION_TIMERS.set(button, timer);
      });
    }

    _getSortedItems() {
      let items = [...this._items];

      // Filter
      if (this._activeFilter === "instock") {
        if (this._inStockFilterState !== "ready") {
          return [];
        }
        items = items.filter((item) => this._inStockEligibleIds.has(item.id));
      }

      // Sort
      if (this._activeFilter === "alpha") {
        items.sort((a, b) => {
          const aTitle = parseWishlistItemTitle(a.title).cleanTitle;
          const bTitle = parseWishlistItemTitle(b.title).cleanTitle;
          return aTitle.localeCompare(bTitle);
        });
      } else if (this._activeFilter === "recent" || this._activeFilter === "default") {
        // "All" (default) and "Recent" both surface the newest additions first so
        // a freshly added product is the first thing visible when the drawer opens.
        if (items.some((item) => getItemAddedAtValue(item) !== null)) {
          items.sort((a, b) => {
            const aAdded = getItemAddedAtValue(a) ?? 0;
            const bAdded = getItemAddedAtValue(b) ?? 0;
            return bAdded - aAdded;
          });
        } else {
          // Legacy payloads did not always include addedAt; preserve the old fallback.
          items.reverse();
        }
      }

      // Always pin newly restocked items to the top
      if (this._newlyRestockedIds.size > 0) {
        const pinned = items.filter((i) => this._newlyRestockedIds.has(i.id));
        const rest = items.filter((i) => !this._newlyRestockedIds.has(i.id));
        items = [...pinned, ...rest];
      }

      return items;
    }

    _renderSorted() {
      if (!this.itemsEl) return;

      this._disconnectPaginationObserver();
      const sorted = this._getSortedItems();

      if (
        this._activeFilter === "instock" &&
        this._inStockFilterState === "loading"
      ) {
        this.itemsEl.innerHTML =
          '<p class="wl-drawer__filter-empty">Checking stock for your wishlist...</p>';
        this.itemsEl.style.display = "";
        this.footerEl?.setAttribute("aria-hidden", "false");
        this.filtersEl?.setAttribute("aria-hidden", "false");
        return;
      }

      if (sorted.length === 0 && this._items.length > 0) {
        // Items exist but are all filtered out
        this.itemsEl.innerHTML =
          '<p class="wl-drawer__filter-empty">No items match this filter.</p>';
        this.itemsEl.style.display = "";
        this.footerEl?.setAttribute("aria-hidden", "false");
        this.filtersEl?.setAttribute("aria-hidden", "false");
        return;
      }

      const fragment = document.createDocumentFragment();
      const visibleItems = sorted.slice(0, this._visibleCount);
      visibleItems.forEach((item) => {
        const el = this._buildItemEl(item);
        if (el) {
          fragment.appendChild(el);
        }
      });

      if (visibleItems.length < sorted.length || this._wishlistHasMore) {
        const sentinel = document.createElement("div");
        sentinel.className = "wl-drawer__load-more-sentinel";
        sentinel.setAttribute("data-wl-load-more-sentinel", "true");
        sentinel.setAttribute("role", "status");
        sentinel.setAttribute("aria-live", "polite");
        sentinel.innerHTML = `
          <span class="wl-drawer__load-more-spinner" aria-hidden="true"></span>
          <span class="visually-hidden">Loading more wishlist items</span>
        `;
        fragment.appendChild(sentinel);
      }

      this.itemsEl.replaceChildren(fragment);

      this._bindItemEvents();
      this._observePaginationSentinel();
    }

    _disconnectPaginationObserver() {
      if (!this._paginationObserver) return;
      this._paginationObserver.disconnect();
      this._paginationObserver = null;
    }

    _observePaginationSentinel() {
      const sentinel = this.itemsEl?.querySelector("[data-wl-load-more-sentinel]");
      if (!sentinel || !("IntersectionObserver" in window)) return;

      this._paginationObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            return;
          }

          this._disconnectPaginationObserver();
          void this._loadMoreVisibleItems();
        },
        {
          root: this.contentEl || null,
          rootMargin: "180px 0px",
          threshold: 0.01,
        },
      );
      this._paginationObserver.observe(sentinel);
    }

    async _fetchNextWishlistPage(loadToken) {
      if (!this._wishlistHasMore || !this._wishlistNextCursor) return false;

      try {
        const payload = await this._fetchPrimaryWishlistData({
          cursor: this._wishlistNextCursor,
          limit: DRAWER_PAGE_SIZE,
          allowDetailedFallback: false,
          allowStale: false,
        });
        if (loadToken !== this._loadToken || !this._open) {
          return false;
        }

        const seenItems = [...this._items];
        const appended = payload.items.filter((item) => {
          if (seenItems.some((seenItem) => wishlistItemsMatchIdentity(seenItem, item))) {
            return false;
          }
          seenItems.push(item);
          return true;
        });
        if (appended.length) {
          this._items = [...this._items, ...appended];
          primeProductCacheFromItems(appended);
        }
        this._wishlistNextCursor = payload.nextCursor || null;
        this._wishlistHasMore = payload.hasMore === true;
        this._wishlistTotalCount = Number.isFinite(Number(payload.totalCount))
          ? Number(payload.totalCount)
          : Math.max(this._wishlistTotalCount || 0, this._items.length);
        this._updateCount(this._wishlistTotalCount || this._items.length);
        this._recomputeAvailabilityState();
        this._applyAvailabilityPayload(this._availabilityCache);
        return appended.length > 0 || this._wishlistHasMore;
      } catch (error) {
        console.warn("[WishlistDrawer] next wishlist page fetch failed", error);
        return false;
      }
    }

    async _loadMoreVisibleItems() {
      const sorted = this._getSortedItems();
      const loadToken = this._loadToken;

      if (this._visibleCount >= sorted.length) {
        const loadedNextPage = await this._fetchNextWishlistPage(loadToken);
        if (!loadedNextPage) return;
      }

      const nextSorted = this._getSortedItems();
      this._visibleCount = Math.min(
        nextSorted.length,
        this._visibleCount + DRAWER_PAGE_SIZE,
      );
      this._renderSorted();
      this._refreshVisibleMissingProducts(loadToken);
    }

    _refreshVisibleMissingProducts(loadToken) {
      if (!this._open || loadToken !== this._loadToken) return;

      const sorted = this._getSortedItems();
      const visibleItems = sorted.slice(
        0,
        Math.min(sorted.length, this._visibleCount + DRAWER_PREFETCH_THRESHOLD),
      );
      const handles = visibleItems
        .filter((item) => itemNeedsProductRefresh(item))
        .map((item) => item.handle);

      void this._refreshProducts(handles, loadToken);
    }

    _buildItemEl(item) {
      const product = getResolvedProduct(item);
      const variants = getResolvedVariants(product);

      const productUrl = item.handle ? `/products/${item.handle}` : "#";
      const imageUrl = getProductImageUrl(item, product);
      const resizedImg = imageUrl ? imgSrc(imageUrl, 200) : "";

      let selectedVariantId =
        extractResourceId(item.variantId) || extractResourceId(variants[0]?.id);
      const selectedVariant = getMatchingVariant(product, selectedVariantId);
      selectedVariantId =
        extractResourceId(selectedVariant?.id) || selectedVariantId;
      const hasSavedVariantFallback =
        variants.length === 0 && Boolean(selectedVariantId);
      const price = getVariantPriceCents(selectedVariant, item.price);
      const comparePrice = getVariantComparePriceCents(selectedVariant);
      const liveAvailability =
        typeof this._currentAvailability[item.id] === "boolean"
          ? this._currentAvailability[item.id]
          : null;
      const available =
        liveAvailability !== null
          ? liveAvailability
          : hasSavedVariantFallback
            ? true
            : isVariantAvailable(selectedVariant);
      const productSoldOut =
        liveAvailability !== null
          ? !liveAvailability
          : variants.length > 0
            ? variants.every((variant) => !isVariantAvailable(variant))
            : hasSavedVariantFallback
              ? false
              : available === false;
      const { cleanTitle, isPickupOnly } = parseWishlistItemTitle(item.title);
      const hasDefaultTitle =
        variants.length === 1 && variants[0]?.title === "Default Title";
      const isNewlyRestocked = this._newlyRestockedIds.has(item.id);
      const quantity = getWishlistItemQuantity(item);
      const productGid = item?.productId
        ? String(item.productId).startsWith("gid://shopify/Product/")
          ? String(item.productId)
          : `gid://shopify/Product/${item.productId}`
        : product?.id
          ? `gid://shopify/Product/${product.id}`
          : "";

      const el = document.createElement("div");
      el.className =
        "wl-drawer-item" +
        (isNewlyRestocked ? " wl-drawer-item--restocked" : "");
      el.dataset.itemId = item.id;
      el.dataset.listId = this._listId;
      el.dataset.productHandle = item.handle || "";
      el.dataset.quantity = String(quantity);
      if (selectedVariantId) {
        el.dataset.variantId = String(selectedVariantId);
      }

      el.innerHTML = `
        ${
          isNewlyRestocked
            ? `<div class="wl-drawer-item__restock-badge" data-restock-badge>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            Back in Stock!
            <button class="wl-drawer-item__restock-dismiss" aria-label="Dismiss notification" data-dismiss-restock>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>`
            : ""
        }

        <button type="button" class="wl-drawer-item__remove" aria-label="Remove ${this._escHtml(cleanTitle)} from wishlist" data-remove>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div class="wl-drawer-item__image-wrap">
          <a href="${productUrl}" class="wl-drawer-item__image-link">
            ${
              resizedImg
                ? `<img src="${resizedImg}" alt="${this._escHtml(cleanTitle)}" class="wl-drawer-item__image" loading="lazy" width="90" height="90">`
                : `<div class="wl-drawer-item__image-placeholder">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
                      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                      <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>`
            }
          </a>
        </div>

        <div class="wl-drawer-item__details">
          <a href="${productUrl}" class="wl-drawer-item__title">${this._escHtml(cleanTitle)}</a>
          ${
            isPickupOnly
              ? `<div class="wl-drawer-item__badge-row">
                  <span class="wl-drawer-item__badge wl-drawer-item__badge--pickup">Pickup Only</span>
                </div>`
              : ""
          }

          <p class="wl-drawer-item__price" data-price>
            ${formatMoney(price)}
            ${
              comparePrice && comparePrice > price
                ? `<span class="wl-drawer-item__price-compare">${formatMoney(comparePrice)}</span>`
                : ""
            }
          </p>

          ${
            !productSoldOut
              ? `<div class="wl-drawer-item__controls">
              ${
                !hasDefaultTitle && variants.length > 1
                  ? `<div class="wl-drawer-item__variant-wrap">
                  <label class="wl-drawer-item__variant-label">Option</label>
                  <select class="wl-drawer-item__variant-select" data-variant-select>
                    ${variants
                      .map(
                        (v) => `
                      <option
                        value="${extractResourceId(v.id)}"
                        data-price="${getVariantPriceCents(v)}"
                        data-compare="${getVariantComparePriceCents(v)}"
                        data-available="${isVariantAvailable(v)}"
                        ${extractResourceId(v.id) === String(selectedVariantId) ? "selected" : ""}
                        ${!isVariantAvailable(v) ? "disabled" : ""}
                      >${this._escHtml(v.title || "Option")} – ${formatMoney(getVariantPriceCents(v))}${!isVariantAvailable(v) ? " – Sold out" : ""}</option>
                    `,
                      )
                      .join("")}
                  </select>
                </div>`
                  : variants.length === 1
                    ? `<input type="hidden" data-variant-select value="${extractResourceId(variants[0].id)}">`
                    : ""
              }

              <div class="wl-drawer-item__qty-row">
                <span class="wl-drawer-item__qty-label">Qty</span>
                <div class="wl-drawer-item__qty-stepper">
                  <button type="button" class="wl-drawer-item__qty-btn" data-qty="-1" aria-label="Decrease quantity" ${quantity <= 1 ? "disabled" : ""}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 12h14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
                  </button>
                  <span class="wl-drawer-item__qty-value" data-qty-value>${quantity}</span>
                  <button type="button" class="wl-drawer-item__qty-btn" data-qty="1" aria-label="Increase quantity" ${quantity >= MAX_WISHLIST_DRAWER_QUANTITY ? "disabled" : ""}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
                  </button>
                </div>
              </div>
            </div>`
              : ""
          }

          ${!available ? `<p class="wl-drawer-item__oos">Currently out of stock</p>` : ""}

          <div class="wl-drawer-item__actions">
            <div class="productitem--action-wrapper wl-drawer-item__action-wrap">
              <div class="productitem--actions productitem--actions-inline wl-item-actions wl-item-actions--productcard">
                <div class="productitem--action atc--button">
                  ${
                    productSoldOut
                      ? `<div
                        class="mm-restock-cta mm-restock-cta--card wl-drawer-item__restock-cta"
                        data-mm-restock-cta
                        data-product-gid="${this._escHtml(productGid)}"
                        data-variant-id="${this._escHtml(selectedVariantId || "")}"
                        data-app-proxy-base="${API_BASE}"
                      >
                        <button
                          type="button"
                          class="productitem--action-trigger productitem--action-atc productitem--action-notify mm-restock-cta__button neumorphic-btn wl-drawer-item__notify-btn"
                          data-mm-restock-btn
                          data-default-text="Notify Me"
                          data-subscribed-text="Alert set"
                          aria-label="Notify me when available"
                          aria-pressed="false"
                        >
                          <span class="atc-button--icon" aria-hidden="true">
                            <span class="restock-bell restock-bell--outline"><i class="fi fi-rr-bell" aria-hidden="true"></i></span>
                            <span class="restock-bell restock-bell--filled"><i class="fi fi-sr-bell" aria-hidden="true"></i></span>
                          </span>
                          <span class="wl-drawer-item__notify-label" data-mm-restock-label>Notify Me</span>
                        </button>
                        <p class="mm-restock-cta__status wl-drawer-item__notify-status" data-mm-restock-status hidden></p>
                      </div>`
                      : `<div class="mm-quick-add">
                        <button
                          type="button"
                          class="productitem--action-trigger productitem--action-atc neumorphic-btn wl-btn-atc"
                          aria-label="Add to cart"
                          data-add-to-cart
                          ${!available ? "disabled" : ""}
                        >
                          <span class="wl-drawer-btn__idle" data-atc-idle>
                            <span class="atc-button--icon atc-button--text-icon" aria-hidden="true">Add to cart</span>
                          </span>
                          <span class="wl-drawer-btn__loading wl-drawer-btn__loading--cart" data-atc-loading aria-hidden="true" hidden>
                            <span class="wl-drawer-btn__spinner wl-drawer-btn__spinner--cart"></span>
                          </span>
                        </button>
                        <button
                          type="button"
                          class="productitem--action-trigger productitem--action-atc productitem--action-buy neumorphic-btn productitem--buy-btn wl-drawer-item__buy-now"
                          aria-label="Buy now"
                          data-buy-now
                          ${!available ? "disabled" : ""}
                        >
                          <span class="atc-button--icon atc-button--text-icon" data-buy-label>Buy Now</span>
                          <span class="wl-drawer-btn__loading wl-drawer-btn__loading--buy" data-buy-loading aria-hidden="true" hidden>
                            <span class="wl-drawer-btn__spinner wl-drawer-btn__spinner--buy"></span>
                          </span>
                        </button>
                      </div>`
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      return el;
    }

    _bindItemEvents() {
      if (!this.itemsEl) return;

      // Dismiss restock badge
      this.itemsEl.querySelectorAll("[data-dismiss-restock]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const itemEl = e.currentTarget.closest(".wl-drawer-item");
          if (!itemEl) return;
          this._dismissRestockBadge(itemEl.dataset.itemId, itemEl);
        });
      });

      // Variant select
      this.itemsEl
        .querySelectorAll("[data-variant-select]")
        .forEach((select) => {
          if (select.tagName !== "SELECT") return;
          select.addEventListener("change", (e) => {
            const item = e.target.closest(".wl-drawer-item");
            if (!item) return;
            const opt = e.target.selectedOptions[0];
            if (!opt) return;

            item.dataset.variantId = opt.value || "";

            const price = parseInt(opt.dataset.price || "0", 10);
            const compare = parseInt(opt.dataset.compare || "0", 10);
            const available = opt.dataset.available !== "false";

            const priceEl = item.querySelector("[data-price]");
            if (priceEl) {
              priceEl.innerHTML = `${formatMoney(price)}${
                compare && compare > price
                  ? `<span class="wl-drawer-item__price-compare">${formatMoney(compare)}</span>`
                  : ""
              }`;
            }

            const addBtn = item.querySelector("[data-add-to-cart]");
            const buyBtn = item.querySelector("[data-buy-now]");
            const oosEl = item.querySelector(".wl-drawer-item__oos");

            if (addBtn) addBtn.disabled = !available;
            if (buyBtn) buyBtn.disabled = !available;
            if (oosEl) oosEl.style.display = available ? "none" : "";
          });
        });

      // Quantity steppers
      this.itemsEl.querySelectorAll("[data-qty]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const item = e.currentTarget.closest(".wl-drawer-item");
          if (!item) return;
          const delta = parseInt(e.currentTarget.dataset.qty, 10);
          const current = this._getQty(item);
          const next = this._normalizeQty(current + delta);
          if (next === current) {
            this._setQty(item, next);
            return;
          }
          this._setQty(item, next);
          this._updateLocalItemQuantity(item.dataset.itemId, next);
          this._queueQuantitySave(item, next, current);
        });
      });

      // Add to Cart
      this.itemsEl.querySelectorAll("[data-add-to-cart]").forEach((btn) => {
        btn.addEventListener("click", async (e) => {
          await this._addToCart(
            e.currentTarget.closest(".wl-drawer-item"),
            false,
          );
        });
      });

      // Buy Now
      this.itemsEl.querySelectorAll("[data-buy-now]").forEach((btn) => {
        btn.addEventListener("click", async (e) => {
          await this._addToCart(
            e.currentTarget.closest(".wl-drawer-item"),
            true,
          );
        });
      });

      // Remove
      this.itemsEl.querySelectorAll("[data-remove]").forEach((btn) => {
        btn.addEventListener("click", async (e) => {
          await this._removeItem(e.currentTarget.closest(".wl-drawer-item"));
        });
      });
    }

    _dismissRestockBadge(itemId, itemEl) {
      this._newlyRestockedIds.delete(itemId);

      const badge = itemEl?.querySelector("[data-restock-badge]");
      if (badge) {
        badge.style.transition = "opacity 0.2s ease, transform 0.2s ease";
        badge.style.opacity = "0";
        badge.style.transform = "translateY(-4px) scale(0.95)";
        setTimeout(() => badge.remove(), 220);
      }
      itemEl?.classList.remove("wl-drawer-item--restocked");

      this._saveRestockState();
    }

    _getVariantId(itemEl) {
      const sel = itemEl.querySelector("[data-variant-select]");
      if (sel) {
        return sel.value || sel.dataset.value || itemEl.dataset.variantId || null;
      }

      // Wishlist items still carry the saved variant id even when product JSON
      // does not provide a selector or hidden input.
      return itemEl.dataset.variantId || null;
    }

    _getQty(itemEl) {
      const el = itemEl.querySelector("[data-qty-value]");
      return this._normalizeQty(el?.textContent || itemEl.dataset.quantity || "1");
    }

    _normalizeQty(value) {
      const parsed =
        typeof value === "number" && Number.isFinite(value)
          ? value
          : Number.parseInt(String(value || ""), 10);
      if (!Number.isFinite(parsed) || parsed < 1) return 1;
      return Math.min(MAX_WISHLIST_DRAWER_QUANTITY, Math.trunc(parsed));
    }

    _setQty(itemEl, quantity) {
      if (!itemEl) return;
      const nextQuantity = this._normalizeQty(quantity);
      itemEl.dataset.quantity = String(nextQuantity);
      const valueEl = itemEl.querySelector("[data-qty-value]");
      if (valueEl) valueEl.textContent = String(nextQuantity);
      itemEl.querySelectorAll("[data-qty]").forEach((btn) => {
        const delta = Number.parseInt(btn.dataset.qty || "0", 10);
        if (delta < 0) {
          btn.disabled = nextQuantity <= 1;
        } else if (delta > 0) {
          btn.disabled = nextQuantity >= MAX_WISHLIST_DRAWER_QUANTITY;
        }
      });
    }

    _updateLocalItemQuantity(itemId, quantity) {
      const normalizedItemId = String(itemId || "").trim();
      if (!normalizedItemId) return;
      const nextQuantity = this._normalizeQty(quantity);
      this._items = this._items.map((item) =>
        String(item?.id || "") === normalizedItemId
          ? { ...item, quantity: nextQuantity }
          : item,
      );
    }

    _findRenderedItem(itemId) {
      const normalizedItemId = String(itemId || "").trim();
      if (!normalizedItemId || !this.itemsEl) return null;
      return (
        Array.from(this.itemsEl.querySelectorAll(".wl-drawer-item")).find(
          (itemEl) => itemEl.dataset.itemId === normalizedItemId,
        ) || null
      );
    }

    _queueQuantitySave(itemEl, quantity, previousQuantity) {
      const itemId = String(itemEl?.dataset?.itemId || "").trim();
      const listId = String(itemEl?.dataset?.listId || this._listId || "").trim();
      if (!itemId || !listId) return;

      const key = `${listId}:${itemId}`;
      const state =
        this._quantitySaveState.get(key) || {
          saving: false,
          pending: null,
          committed: this._normalizeQty(previousQuantity),
        };
      state.pending = this._normalizeQty(quantity);
      this._quantitySaveState.set(key, state);
      setStoredWishlistQuantity(listId, itemId, state.pending);
      void this._flushQuantitySave(key, listId, itemId);
    }

    async _flushQuantitySave(key, listId, itemId) {
      const state = this._quantitySaveState.get(key);
      if (!state || state.saving) return;
      state.saving = true;

      try {
        while (state.pending !== null) {
          const quantity = state.pending;
          state.pending = null;
          setStoredWishlistQuantity(listId, itemId, quantity);
          const savedItem = await fetchJSON(buildListItemsUrl(listId), {
            method: "PATCH",
            body: JSON.stringify({ id: itemId, quantity }),
          });
          const savedQuantity = this._normalizeQty(savedItem?.quantity ?? quantity);
          const serverSavedQuantity = hasExplicitWishlistItemQuantity(savedItem);
          const storedQuantity = getStoredWishlistQuantity(listId, itemId);
          if (
            state.pending === null &&
            storedQuantity !== null &&
            storedQuantity !== quantity
          ) {
            state.pending = storedQuantity;
          }
          const hasNewerPendingQuantity = state.pending !== null;
          state.committed = savedQuantity;
          if (!hasNewerPendingQuantity) {
            if (serverSavedQuantity) {
              clearStoredWishlistQuantity(listId, itemId);
            }
            this._updateLocalItemQuantity(itemId, savedQuantity);
            this._setQty(this._findRenderedItem(itemId), savedQuantity);
          }
          this._clearListItemsCache(listId);
          await invalidateSharedWishlistCaches({ invalidateProfile: false });
          dispatchWishlistListsUpdated({
            action: "update-item-quantity",
            listId,
            itemId,
            quantity: savedQuantity,
            skipDrawerReload: true,
          });
        }
      } catch (error) {
        if (state.pending === null) {
          const storedQuantity = getStoredWishlistQuantity(listId, itemId);
          const hasStoredPendingQuantity =
            storedQuantity !== null && storedQuantity !== state.committed;
          const fallbackQuantity = hasStoredPendingQuantity
            ? storedQuantity
            : state.committed;
          this._updateLocalItemQuantity(itemId, fallbackQuantity);
          this._setQty(this._findRenderedItem(itemId), fallbackQuantity);
          if (!hasStoredPendingQuantity) {
            setStoredWishlistQuantity(listId, itemId, state.committed);
          }
        }
        console.error("[WishlistDrawer] quantity save error", error);
        Toast.show(error?.message || "Couldn't save quantity. Please try again.", "error");
      } finally {
        state.saving = false;
        if (state.pending !== null) {
          void this._flushQuantitySave(key, listId, itemId);
        } else {
          this._quantitySaveState.delete(key);
        }
      }
    }

    _setButtonLoadingState(button, buyNow, isLoading) {
      if (!button) return;

      button.classList.toggle("loading", isLoading);
      button.setAttribute("aria-busy", isLoading ? "true" : "false");

      if (buyNow) {
        const buyLabel = button.querySelector("[data-buy-label]");
        const buyLoad = button.querySelector("[data-buy-loading]");
        if (buyLabel && buyLoad) {
          buyLabel.hidden = isLoading;
          buyLoad.hidden = !isLoading;
        }
        return;
      }

      const idle = button.querySelector("[data-atc-idle]");
      const load = button.querySelector("[data-atc-loading]");
      if (idle && load) {
        idle.hidden = isLoading;
        load.hidden = !isLoading;
      }
    }

    async _addToCart(itemEl, buyNow) {
      if (!itemEl) return;
      const variantId = extractResourceId(this._getVariantId(itemEl));
      const qty = this._getQty(itemEl);
      const parsedVariantId = Number.parseInt(variantId, 10);

      if (!variantId || !Number.isFinite(parsedVariantId)) {
        Toast.show("Please select a variant first.", "error");
        return;
      }

      const addBtn = itemEl.querySelector("[data-add-to-cart]");
      const buyBtn = itemEl.querySelector("[data-buy-now]");
      const activeBtn = buyNow ? buyBtn : addBtn;

      if (activeBtn) {
        this._setButtonLoadingState(activeBtn, buyNow, true);
      }

      try {
        if (buyNow) {
          if (!window.MrMapleSingleItemCheckout || typeof window.MrMapleSingleItemCheckout.start !== "function") {
            throw new Error("Checkout is still loading. Please try again.");
          }

          await window.MrMapleSingleItemCheckout.start({
            source: "wishlist-drawer",
            item: { id: parsedVariantId, quantity: qty },
          });
          return;
        }

        await fetchJSON("/cart/add.js", {
          method: "POST",
          body: JSON.stringify({ id: parsedVariantId, quantity: qty }),
        });

        if (itemEl.dataset.itemId && this._newlyRestockedIds.has(itemEl.dataset.itemId)) {
          this._dismissRestockBadge(itemEl.dataset.itemId, itemEl);
        }

        document.dispatchEvent(new CustomEvent("cart:update"));
        document.dispatchEvent(new CustomEvent("cart:updated"));
        Toast.show("Added to cart!", "success");
      } catch (err) {
        console.error("[WishlistDrawer] add to cart error", err);
        Toast.show(err?.message || "Couldn't add to cart. Please try again.", "error");
      } finally {
        if (activeBtn) {
          this._setButtonLoadingState(activeBtn, buyNow, false);
        }
      }
    }

    _buildUndoPayload(item) {
      if (!item || typeof item !== "object") return null;
      const productId =
        typeof item.productId === "string" && item.productId.trim()
          ? item.productId.trim()
          : "";
      if (!productId) return null;

      return {
        productId,
        variantId:
          typeof item.variantId === "string" && item.variantId.trim()
            ? item.variantId.trim()
            : undefined,
        title:
          typeof item.title === "string" && item.title.trim()
            ? item.title.trim()
            : "Untitled Product",
        handle:
          typeof item.handle === "string" && item.handle.trim()
            ? item.handle.trim()
            : undefined,
        vendor:
          typeof item.vendor === "string" && item.vendor.trim()
            ? item.vendor.trim()
            : undefined,
        productType:
          typeof item.productType === "string" && item.productType.trim()
            ? item.productType.trim()
            : undefined,
        tags: Array.isArray(item.tags)
          ? item.tags
              .map((value) =>
                typeof value === "string" ? value.trim() : String(value || "").trim(),
              )
              .filter(Boolean)
          : undefined,
        price:
          typeof item.price === "number" && Number.isFinite(item.price)
            ? item.price
            : undefined,
        quantity:
          typeof item.quantity === "number" && Number.isFinite(item.quantity)
            ? getWishlistItemQuantity(item)
            : undefined,
        compareAtPrice:
          typeof item.compareAtPrice === "number" &&
          Number.isFinite(item.compareAtPrice)
            ? item.compareAtPrice
            : undefined,
        currency:
          typeof item.currency === "string" && item.currency.trim()
            ? item.currency.trim()
            : undefined,
        image: item.image || undefined,
        images: Array.isArray(item.images)
          ? item.images.filter(Boolean)
          : undefined,
        variantTitle:
          typeof item.variantTitle === "string" && item.variantTitle.trim()
            ? item.variantTitle.trim()
            : undefined,
        note:
          Object.prototype.hasOwnProperty.call(item, "note") && item.note != null
            ? String(item.note)
            : undefined,
        addedAt:
          typeof item.addedAt === "number" && Number.isFinite(item.addedAt)
            ? item.addedAt
            : undefined,
        originUid:
          typeof item.originUid === "string" && item.originUid.trim()
            ? item.originUid.trim()
            : undefined,
        originShop:
          typeof item.originShop === "string" && item.originShop.trim()
            ? item.originShop.trim()
            : undefined,
        originCustomerId:
          typeof item.originCustomerId === "string" && item.originCustomerId.trim()
            ? item.originCustomerId.trim()
            : undefined,
        originCustomerGid:
          typeof item.originCustomerGid === "string" && item.originCustomerGid.trim()
            ? item.originCustomerGid.trim()
            : undefined,
      };
    }

    async _restoreRemovedItem(listId, removedItem, insertIndex) {
      const payload = this._buildUndoPayload(removedItem);
      if (!payload) {
        throw new Error("Unable to restore this item.");
      }

      const restoredItem = await fetchJSON(`${API_BASE}/v1/lists/${listId}/items`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const restoredItemId = String(
        restoredItem?.id || restoredItem?.itemId || "",
      ).trim();
      this._clearPendingWishlistRemove(listId, {
        itemId: removedItem?.id || removedItem?.itemId,
        item: removedItem,
        productId: removedItem?.productId,
        productGid: removedItem?.productGid,
        variantId: removedItem?.variantId,
      });

      if (restoredItemId && Number.isInteger(insertIndex)) {
        const orderedItemIds = this._items
          .map((entry) => String(entry?.id || "").trim())
          .filter(Boolean);
        const targetIndex = Math.max(0, Math.min(insertIndex, orderedItemIds.length));
        orderedItemIds.splice(targetIndex, 0, restoredItemId);

        await fetchJSON(`${API_BASE}/v1/lists/${listId}/items`, {
          method: "PATCH",
          body: JSON.stringify({ orderedItemIds }),
        });
      }

      dispatchWishlistItemAdded({
        listId,
        item: restoredItem,
        productId: restoredItem?.productId || removedItem?.productId,
        productGid: restoredItem?.productGid || removedItem?.productGid,
        variantId: restoredItem?.variantId || removedItem?.variantId,
        skipDrawerApply: true,
      });
      await invalidateSharedWishlistCaches();
      dispatchWishlistListsUpdated({
        action: "restore-item",
        listId: listId || this._listId || PRIMARY_WISHLIST_LIST_ID,
        itemId:
          restoredItem?.id ||
          restoredItem?.itemId ||
          removedItem?.id ||
          removedItem?.itemId,
      });
      await this._load({ force: true });
      return restoredItem;
    }

    async _removeItem(itemEl) {
      if (!itemEl) return;
      const itemId = itemEl.dataset.itemId;
      const listId = itemEl.dataset.listId;
      if (!itemId || !listId) return;
      const removeKey = buildDeleteItemKey(listId, itemId);
      if (!removeKey || this._removeRequests.has(removeKey) || itemEl.dataset.removing === "1") {
        return;
      }
      this._removeRequests.add(removeKey);
      itemEl.dataset.removing = "1";
      const removeButton = itemEl.querySelector("[data-remove]");
      if (removeButton) {
        removeButton.disabled = true;
      }
      const removedItemIndex = this._items.findIndex(
        (entry) => String(entry?.id || "") === String(itemId),
      );
      const removedItemSnapshot =
        removedItemIndex >= 0
          ? JSON.parse(JSON.stringify(this._items[removedItemIndex]))
          : null;
      console.info("[WishlistDrawer] remove start", {
        itemId,
        listId,
        removedItemIndex,
        removedItem: removedItemSnapshot,
        currentListId: this._listId,
        currentItemsCount: this._items.length,
      });

      // Remove from restock tracking if present
      if (this._newlyRestockedIds.has(itemId)) {
        this._newlyRestockedIds.delete(itemId);
        this._saveRestockState();
      }

      // Optimistic removal
      itemEl.style.transition = "opacity 0.25s ease, transform 0.25s ease";
      itemEl.style.opacity = "0";
      itemEl.style.transform = "translateX(20px)";

      const previousItemsLength = this._items.length;
      this._items = this._items.filter((i) => i.id !== itemId);
      const removedFromLoaded = previousItemsLength - this._items.length;
      if (removedFromLoaded > 0) {
        // Decrement the true total, not the loaded-page length. The drawer only
        // holds the first DRAWER_PAGE_SIZE items, so _items.length undercounts
        // wishlists larger than one page.
        const baseTotal = Number.isFinite(Number(this._wishlistTotalCount))
          ? Number(this._wishlistTotalCount)
          : previousItemsLength;
        this._wishlistTotalCount = Math.max(
          this._items.length,
          baseTotal - removedFromLoaded,
        );
      }
      this._updateCount(this._wishlistTotalCount);

      if (this._items.length === 0) {
        setTimeout(() => {
          itemEl.remove();
          this._showState("empty");
        }, 300);
      } else {
        setTimeout(() => this._renderSorted(), 260);
      }

      const removedItemDetail = {
        itemId,
        listId,
        item: removedItemSnapshot || undefined,
        productId: removedItemSnapshot?.productId,
        productGid: removedItemSnapshot?.productGid,
        variantId: removedItemSnapshot?.variantId,
        optimistic: true,
        action: "remove-item",
        skipDrawerApply: true,
      };
      this._recordPendingWishlistRemove(listId, removedItemDetail);
      this._clearListItemsCache(listId);
      dispatchWishlistItemRemoved(removedItemDetail);

      let removeSucceeded = false;
      try {
        await deleteWishlistDrawerItem(listId, itemId, {
          source: "drawer-remove",
          removedItemIndex,
          productId: removedItemSnapshot?.productId,
          variantId: removedItemSnapshot?.variantId,
        });
        removeSucceeded = true;

        await invalidateSharedWishlistCaches();
        dispatchWishlistListsUpdated({
          action: "remove-item",
          listId:
            removedItemSnapshot?.listId ||
            listId ||
            this._listId ||
            PRIMARY_WISHLIST_LIST_ID,
          itemId:
            removedItemSnapshot?.id ||
            removedItemSnapshot?.itemId ||
            itemId,
          skipDrawerReload: true,
        });
        if (removedItemSnapshot) {
          Toast.show("Item removed", "default", {
            actionLabel: "Undo",
            actionAriaLabel: "Undo wishlist drawer removal",
            closeOnAction: false,
            onAction: async () => {
              try {
                await this._restoreRemovedItem(
                  listId,
                  removedItemSnapshot,
                  removedItemIndex,
                );
                Toast.show("Item restored", "success");
              } catch (restoreError) {
                console.error("[WishlistDrawer] restore error", restoreError);
                Toast.show("Couldn't restore item. Please try again.", "error");
                throw restoreError;
              }
            },
          });
        } else {
          Toast.show("Item removed", "default");
        }
      } catch (err) {
        console.error("[WishlistDrawer] remove error", {
          error: err,
          itemId,
          listId,
          removedItemIndex,
          removedItem: removedItemSnapshot,
        });
        this._clearPendingWishlistRemove(listId, removedItemDetail);
        if (removedItemSnapshot) {
          dispatchWishlistItemAdded({
            listId,
            item: removedItemSnapshot,
            productId: removedItemSnapshot?.productId,
            productGid: removedItemSnapshot?.productGid,
            variantId: removedItemSnapshot?.variantId,
            rollback: true,
            skipDrawerApply: true,
          });
        }
        Toast.show("Couldn't remove item. Please try again.", "error");
        this._load();
      } finally {
        this._removeRequests.delete(removeKey);
        if (!removeSucceeded) {
          delete itemEl.dataset.removing;
          if (removeButton) {
            removeButton.disabled = false;
          }
        }
      }
    }

    async _clearAll() {
      if (!this._items.length) return;

      const totalToClear = Math.max(
        this._items.length,
        Number(this._wishlistTotalCount) || 0,
      );
      const confirmed = window.confirm(
        `Remove all ${totalToClear} item${totalToClear !== 1 ? "s" : ""} from your wishlist?`,
      );
      if (!confirmed) return;

      const itemsToRemove = this._items.slice();
      const listId = this._listId;

      // Optimistic clear
      this._items = [];
      this._wishlistTotalCount = 0;
      this._updateCount(0);
      this._newlyRestockedIds.clear();
      this._saveRestockState();

      const itemEls = this.itemsEl?.querySelectorAll(".wl-drawer-item") ?? [];
      itemEls.forEach((el) => {
        el.style.transition = "opacity 0.2s ease, transform 0.2s ease";
        el.style.opacity = "0";
        el.style.transform = "translateX(20px)";
      });
      setTimeout(() => this._showState("empty"), 260);

      itemsToRemove.forEach((item) => {
        dispatchWishlistItemRemoved({
          itemId: item?.id || item?.itemId,
          listId: item?.listId || listId || PRIMARY_WISHLIST_LIST_ID,
          item,
          productId: item?.productId,
          productGid: item?.productGid,
          variantId: item?.variantId,
          optimistic: true,
          action: "clear-list",
          skipDrawerApply: true,
        });
      });

      const rollbackClearedItems = () => {
        itemsToRemove.forEach((item) => {
          dispatchWishlistItemAdded({
            listId: item?.listId || listId || PRIMARY_WISHLIST_LIST_ID,
            item,
            productId: item?.productId,
            productGid: item?.productGid,
            variantId: item?.variantId,
            rollback: true,
            action: "clear-list-rollback",
            skipDrawerApply: true,
          });
        });
      };

      try {
        // Single bulk request: one rate-limit token + batched server-side
        // deletes. Avoids the old fire-N-parallel-DELETEs pattern that tripped
        // the write rate limiter (HTTP 429) on large wishlists.
        await fetchJSON(buildListItemsUrl(listId), {
          method: "DELETE",
          body: JSON.stringify({ clearAll: true }),
        });
        await invalidateSharedWishlistCaches();
        dispatchWishlistListsUpdated({
          action: "clear-list",
          listId: listId || PRIMARY_WISHLIST_LIST_ID,
          skipDrawerReload: true,
        });
        Toast.show("Wishlist cleared", "default");
      } catch (err) {
        // Older server deploys don't accept { clearAll: true } (400/404/405).
        // Fall back to throttled per-item deletes that respect the rate limiter.
        const status = Number(err?.status);
        if (status === 400 || status === 404 || status === 405) {
          try {
            await this._clearAllSequential(listId, itemsToRemove);
            await invalidateSharedWishlistCaches();
            dispatchWishlistListsUpdated({
              action: "clear-list",
              listId: listId || PRIMARY_WISHLIST_LIST_ID,
              skipDrawerReload: true,
            });
            Toast.show("Wishlist cleared", "default");
            return;
          } catch (fallbackErr) {
            console.error("[WishlistDrawer] clear all fallback error", fallbackErr);
          }
        } else {
          console.error("[WishlistDrawer] clear all error", err);
        }
        rollbackClearedItems();
        Toast.show("Couldn't clear wishlist. Please try again.", "error");
        this._load({ force: true });
      }
    }

    async _clearAllSequential(listId, itemsToRemove) {
      // Concurrency 1 with backoff on 429 keeps within the server write limit.
      for (const item of itemsToRemove) {
        let attempt = 0;
        for (;;) {
          try {
            await deleteWishlistDrawerItem(listId, item.id, {
              source: "drawer-clear-all-fallback",
              productId: item.productId,
              variantId: item.variantId,
            });
            break;
          } catch (err) {
            if (Number(err?.status) === 429 && attempt < 5) {
              const delay = Math.min(2000, 250 * 2 ** attempt);
              await new Promise((resolve) => setTimeout(resolve, delay));
              attempt += 1;
              continue;
            }
            throw err;
          }
        }
      }
    }

    _escHtml(str) {
      return String(str || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init() {
    const drawer = new WishlistDrawer();
    drawer.init();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
